import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_TRANSFER_NOTE, applySimpleOffice, applySimpleSettings, computeDocument, computeFeeCalc, formatJPY, formatWareki, isFeeScheduleDocType, serializeBlocksToHtml, type FeeCalcInput, type FeeCalcResult, type ProfessionWithholdingMap, type SimpleSettings, type TemplateRenderData } from "@juro/shared";
import { casesApi, documentsApi, feeSchedulesApi, paymentsApi, templatesApi, getDocTypeLabel, type DocumentSummary, type DocumentTemplate, type LineItem, type NewLineItem } from "../api.js";
import { emptyFeeCalcInput } from "../lib/feeCalcDefaults.js";
import { WorkRateCalcEditor } from "../components/calc-sheets/WorkRateCalcEditor.js";
import { GuaranteeCalcEditor } from "../components/calc-sheets/GuaranteeCalcEditor.js";
import { createBlankTemplate } from "../lib/createBlankTemplate.js";
import { markIssued } from "../lib/recentIssues.js";
import { clearDocQueue, getDocQueue, nextPendingDocId, queuePosition } from "../lib/documentQueue.js";
import { DocumentQueueNav } from "../components/document/DocumentQueueNav.js";
import { collectIssuerRefs, issuerRefLabel, resolveIssuerRef } from "../lib/issuerMaster.js";
import { useDocTypeProfession, useTenantDocumentTypes } from "../hooks/useDocTypeProfession.js";
import { tenantProfessionsApi } from "../api/tenantProfessionsApi.js";
import { IssuerMasterDialog } from "../components/editor/IssuerMasterDialog.js";
import type { AnyBlock, GridBlock, GridColumnDef } from "@juro/shared";
import { Modal } from "../components/Modal.js";
import { StatusBadge } from "../components/StatusBadge.js";
import { toast } from "../components/Toast.js";
import { btnStyle } from "../App.js";
// M0: 子コンポーネント
import { DocumentHeader } from "../components/document/DocumentHeader.js";
import { DocumentStatusGuide } from "../components/document/DocumentStatusGuide.js";
import { DocumentPreview } from "../components/document/DocumentPreview.js";
import { AmountSummary } from "../components/document/AmountSummary.js";
import { EstimateSyncCard, type EstimateApiError } from "../components/document/EstimateSyncCard.js";
import { Button } from "../components/ui/Button.js";
// M1: 内容編集フォーム
import { InvoiceContentForm } from "../components/document/invoice-content/InvoiceContentForm.js";
import { SimpleTemplateDrawer } from "../components/document/simple-template/SimpleTemplateDrawer.js";
import { useScreenView } from "../lib/analytics.js";

// Phase 3（document-type-master-phase3.md §1.9）: 資格ラベル・見積書の関連書類候補は
// 既存3資格の固定配列から tenant_professions / tenant_document_types 由来の動的な値に
// 置き換えた（PROFESSION_LABEL・RELATED_INVOICE_TYPES 定数は撤去）。

// 発行済み書類の確定PDFを取得する。発行時のPDF確定に失敗していた（pdf_path 未設定の）
// 過去データでは 404 になるため、その場合のみ初回生成にフォールバックする。
async function fetchIssuedPdf(docId: string): Promise<Blob> {
  try {
    return await documentsApi.getPdf(docId);
  } catch {
    return await documentsApi.generatePdf(docId);
  }
}

// ── テンプレート由来のカラムを line_item へ割り付けるロール推定 ──────────
type CellRole = "profession" | "description" | "date" | "fee" | "tax" | "none";

function pickGridBlock(template: DocumentTemplate | undefined): GridBlock | null {
  const blocks = template?.template_json?.body?.blocks;
  if (!blocks) return null;
  for (const block of blocks) {
    if (block.type === "grid") return block;
  }
  return null;
}

function assignColumnRoles(columns: GridColumnDef[]): CellRole[] {
  const roles: CellRole[] = new Array<CellRole>(columns.length).fill("none");
  const used = { profession: false, description: false, date: false, fee: false, tax: false };
  const txt = (c: GridColumnDef) => `${c.id} ${c.header}`.toLowerCase();

  // 1) 日付列（format=date またはヘッダ/id に日付ワード）
  columns.forEach((c, i) => {
    if (roles[i] !== "none") return;
    const s = txt(c);
    if (!used.date && (c.format === "date" || /date|日付|年月日/.test(s))) {
      roles[i] = "date";
      used.date = true;
    }
  });

  // 2) 業種列（「業種」「profession」「prof_」のみ。「作業種別」「種別」は description とする）
  columns.forEach((c, i) => {
    if (roles[i] !== "none") return;
    const s = txt(c);
    if (!used.profession && /\bprofession\b|\bprof_|\b業種\b/.test(s)) {
      roles[i] = "profession";
      used.profession = true;
    }
  });

  // 3) 立替・登録税列（明確な税/立替キーワードを含む通貨列）
  columns.forEach((c, i) => {
    if (roles[i] !== "none" || c.format !== "currency") return;
    const s = txt(c);
    if (!used.tax && /登録税|印紙|立替|実費|郵送|送料|pass_through|tax_amount/.test(s)) {
      roles[i] = "tax";
      used.tax = true;
    }
  });

  // 4) 報酬列:
  //    a) formula 付き通貨列を優先（「計」「amount」= 最終算出金額）
  //    b) 報酬キーワードを含む通貨列
  //    c) 残り通貨列の先頭（→ fee）、次（→ tax）
  columns.forEach((c, i) => {
    if (roles[i] !== "none" || c.format !== "currency" || used.fee) return;
    if (c.formula) { roles[i] = "fee"; used.fee = true; }
  });
  columns.forEach((c, i) => {
    if (roles[i] !== "none" || c.format !== "currency" || used.fee) return;
    if (/fee|報酬|手数料|料金|amount/.test(txt(c))) { roles[i] = "fee"; used.fee = true; }
  });
  columns.forEach((c, i) => {
    if (roles[i] !== "none" || c.format !== "currency") return;
    if (!used.fee) { roles[i] = "fee"; used.fee = true; return; }
    if (!used.tax) { roles[i] = "tax"; used.tax = true; return; }
  });

  // 5) テキスト列: 先頭を description に（2列目以降は none のまま）
  columns.forEach((c, i) => {
    if (roles[i] !== "none") return;
    const fmt = c.format ?? "text";
    if (fmt === "text" && !used.description) {
      roles[i] = "description";
      used.description = true;
    }
  });

  return roles;
}

type TemplateData = Record<string, unknown>;

function getPath(ctx: TemplateData, path: string): unknown {
  if (Object.prototype.hasOwnProperty.call(ctx, path)) return ctx[path];
  return path.split(".").reduce<unknown>((value, part) => {
    if (value && typeof value === "object" && part in value) {
      return (value as Record<string, unknown>)[part];
    }
    return undefined;
  }, ctx);
}

function interpolateTemplateValue(value: unknown, data: TemplateData): string {
  if (value === null || value === undefined) return "";
  if (typeof value !== "string") return String(value);
  return value.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, path: string) => {
    const resolved = getPath(data, path.trim());
    return resolved === null || resolved === undefined ? "" : String(resolved);
  });
}

function formatTemplateValue(value: unknown, format?: string): string {
  if (value === null || value === undefined || value === "") return "";
  if (format === "currency" || typeof value === "number") return formatJPY(Number(value));
  return String(value);
}

function issuerDefaults(docType: string, issuedByName?: string | null) {
  const isAmano = docType === "judicial_invoice" || issuedByName === "天野晃浩";
  return {
    qualification: isAmano ? "司法書士" : "行政書士・土地家屋調査士",
    business: "",
    business_label: "",
    name: issuedByName || (isAmano ? "天野晃浩" : "長坂宏子"),
    registration_number: "",
    rules: { registration_number: "" },
  };
}

function buildTemplateData(
  doc: import("../api.js").DocumentDetail,
  caseDetail: import("../api.js").CaseDetail | undefined,
): TemplateData {
  const issuer = issuerDefaults(doc.doc_type, doc.issued_by_name);
  const invoiceRegistrationNumber =
    issuer.name === "天野晃浩" ? "T7810948913857" : "T8810949116170";
  const date = doc.issued_at
    ? new Date(doc.issued_at).toLocaleDateString("ja-JP")
    : new Date().toLocaleDateString("ja-JP");

  return {
    client: { name: caseDetail?.client_name ?? "" },
    office: {
      name: "登記測量 長坂・天野合同事務所",
      name_short: "登記測量",
      name_main: "長坂・天野合同事務所",
      postal: "444-0853",
      address: "岡崎市三崎町9番地5",
      tel: "0564-52-8530",
      fax: "0564-51-7996",
    },
    invoice: {
      number: doc.doc_number ?? "（未採番）",
      registration_number: invoiceRegistrationNumber,
      date,
    },
    issuer,
    transaction: { date },
    amount: {
      invoice: doc.computed.total,
      fee: doc.computed.fee_subtotal,
      consumption_tax: doc.computed.consumption_tax,
      withholding: doc.computed.withholding_tax,
    },
    detail: {
      subtotal: doc.computed.fee_subtotal,
      tax: doc.computed.consumption_tax,
      total: doc.computed.total,
    },
  };
}

function computedBlockValue(block: Extract<AnyBlock, { type: "computed_value" }>, data: TemplateData): number {
  if (block.data_key) return Math.trunc(Number(getPath(data, block.data_key) ?? 0));
  if (block.formula.includes("detail_total")) return Math.trunc(Number(getPath(data, "detail.total") ?? 0));
  if (block.formula.includes("detail_subtotal")) return Math.trunc(Number(getPath(data, "detail.subtotal") ?? 0));
  if (block.formula.includes("consumption_tax")) return Math.trunc(Number(getPath(data, "amount.consumption_tax") ?? 0));
  return 0;
}

function evaluateSummaryCell(raw: unknown, data: TemplateData): unknown {
  if (typeof raw !== "string" || !raw.trim().startsWith("=")) return raw;
  const formula = raw.trim();
  if (/detail_total\(\)|\{detail\.total\}|\{amount\.invoice\}/.test(formula)) return getPath(data, "detail.total");
  if (/detail_subtotal\(\)|sum\(rows|\{detail\.subtotal\}|\{amount\.fee\}/.test(formula)) return getPath(data, "detail.subtotal");
  if (/consumption_tax|\{detail\.tax\}|\{amount\.consumption_tax\}/.test(formula)) return getPath(data, "detail.tax");
  if (/withholding|\{amount\.withholding\}/.test(formula)) return getPath(data, "amount.withholding");
  return "";
}

// ── 明細行エディタ ────────────────────────────────────────────────────────
type LineRow = NewLineItem & { _key: number };

function toNewLineItems(items: LineRow[]): NewLineItem[] {
  return items.map((item, index) => ({
    line_order: item.line_order || index + 1,
    profession: item.profession ?? null,
    item_date: item.item_date || undefined,
    description: item.description,
    fee_amount: Math.floor(Number(item.fee_amount) || 0),
    tax_amount: Math.floor(Number(item.tax_amount) || 0),
  }));
}

function toLineRows(items: LineItem[]): LineRow[] {
  return items.map((item) => ({
    line_order: item.line_order,
    profession: item.profession,
    item_date: item.item_date ?? undefined,
    description: item.description,
    fee_amount: item.fee_amount,
    tax_amount: item.tax_amount,
    _key: Date.now() + item.line_order,
  }));
}

function LineItemEditor({
  lines, docType, onChange, templateGrid, editable = true,
}: {
  lines:         LineRow[];
  docType:       string;
  onChange:      (lines: LineRow[]) => void;
  templateGrid?: GridBlock | null;
  editable?:     boolean;
}) {
  const isEstimate = docType === "estimate";

  // reviewer 指摘（Phase 3レビュー必須3）: 見積書グリッドの資格セレクタが
  // judicial/administrative/surveyor の3値ハードコードのままだと、カスタム資格の
  // 明細行（profession='custom_xxxx'）がどの option にも一致せず空欄表示になり、
  // ユーザーがそのセルを一度触ると既存3資格に書き換わってしまう。
  // tenant_professions から動的に選択肢を組み立てる。
  const professionsQuery = useQuery({
    queryKey: ["tenant-professions"],
    queryFn: () => tenantProfessionsApi.list(),
  });
  const professionOptions = professionsQuery.data?.data ?? [];
  function renderProfessionOptions(currentValue: string | null | undefined) {
    const known = professionOptions.some((p) => p.profession === currentValue);
    return (
      <>
        <option value="">—</option>
        {/* 既存値がリストに無い（削除済み・未取得等）場合でも選択肢から消えないようにする */}
        {!known && currentValue && <option value={currentValue}>{currentValue}</option>}
        {professionOptions.map((p) => (
          <option key={p.profession} value={p.profession}>{p.qualification_label}</option>
        ))}
      </>
    );
  }

  // テンプレート列を優先。マッピング可能な列が1つもない場合はフォールバック
  const tplCols  = templateGrid?.columns ?? null;
  const tplRoles = tplCols ? assignColumnRoles(tplCols) : null;
  const useTemplate = !!tplCols && !!tplRoles && tplRoles.some((r) => r !== "none");
  const hasUnmapped  = !!tplRoles && tplRoles.some((r) => r === "none");

  function addRow() {
    const maxOrder = Math.max(0, ...lines.map((l) => l.line_order));
    onChange([...lines, { _key: Date.now(), line_order: maxOrder + 1, description: "", fee_amount: 0, tax_amount: 0, profession: null }]);
  }

  function removeRow(key: number) {
    onChange(lines.filter((l) => l._key !== key).map((l, i) => ({ ...l, line_order: i + 1 })));
  }

  function update(key: number, patch: Partial<LineRow>) {
    onChange(lines.map((l) => l._key === key ? { ...l, ...patch } : l));
  }

  function focusCell(rowIndex: number, colIndex: number) {
    const row = Math.max(0, Math.min(lines.length - 1, rowIndex));
    const col = Math.max(0, colIndex);
    const target = document.querySelector<HTMLInputElement | HTMLSelectElement>(
      `[data-line-cell="${row}:${col}"]`,
    );
    target?.focus();
    if (target instanceof HTMLInputElement) target.select();
  }

  function handleCellKeyDown(
    event: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
    rowIndex: number,
    colIndex: number,
  ) {
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      event.preventDefault();
      addRow();
      window.setTimeout(() => focusCell(lines.length, colIndex), 0);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      focusCell(rowIndex + (event.shiftKey ? -1 : 1), colIndex);
      return;
    }
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      focusCell(rowIndex + (event.key === "ArrowUp" ? -1 : 1), colIndex);
      return;
    }
    const inp = event.currentTarget instanceof HTMLInputElement ? event.currentTarget : null;
    if (inp && event.key === "ArrowLeft" && inp.selectionStart === 0) {
      event.preventDefault();
      focusCell(rowIndex, colIndex - 1);
      return;
    }
    if (inp && event.key === "ArrowRight" && inp.selectionStart === inp.value.length) {
      event.preventDefault();
      focusCell(rowIndex, colIndex + 1);
    }
  }

  // ─── テンプレートカラムレイアウト ─────────────────────────────────────
  if (useTemplate && tplCols && tplRoles) {
    return (
      <div>
        {/* 非対応列が含まれる場合の案内 */}
        {hasUnmapped && (
          <div style={{
            marginBottom: 10, padding: "8px 12px",
            background: "#fffbeb", border: "1px solid #fcd34d",
            borderRadius: 6, fontSize: 12, color: "#92400e",
          }}>
            ⚠ 件数・基準・加減率など計算式列はこの画面で直接入力できません。
            「計」「報酬額」列に最終金額を入力してください。
          </div>
        )}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, tableLayout: "auto", minWidth: 480 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                {tplCols.map((col, ci) => {
                  const role     = tplRoles[ci] ?? "none";
                  const isRight  = role === "fee" || role === "tax" || col.format === "currency" || col.format === "number" || col.format === "percent";
                  const isMapped = role !== "none";
                  return (
                    <th key={col.id} style={{
                      ...thStyle,
                      textAlign:  isRight ? "right" : "left",
                      color:      isMapped ? "#475569" : "#94a3b8",
                      whiteSpace: "nowrap",
                    }}>
                      {col.header}
                      {!isMapped && (
                        <span style={{ marginLeft: 3, fontSize: 10, color: "#94a3b8" }}>（自動）</span>
                      )}
                    </th>
                  );
                })}
                <th style={{ ...thStyle, width: 28 }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, rowIndex) => (
                <tr key={l._key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  {tplCols.map((col, ci) => {
                    const role    = tplRoles[ci] ?? "none";
                    const isRight = role === "fee" || role === "tax";

                    const cellContent = (() => {
                      switch (role) {
                        case "profession":
                          return (
                            <select
                              value={l.profession ?? ""}
                              disabled={!editable}
                              onChange={(e) => update(l._key, { profession: e.target.value as LineRow["profession"] || null })}
                              data-line-cell={`${rowIndex}:${ci}`}
                              onKeyDown={(e) => handleCellKeyDown(e, rowIndex, ci)}
                              style={inputStyle}
                            >
                              {renderProfessionOptions(l.profession)}
                            </select>
                          );
                        case "date":
                          return (
                            <input
                              type="date"
                              value={l.item_date ?? ""}
                              disabled={!editable}
                              onChange={(e) => update(l._key, { item_date: e.target.value || undefined })}
                              data-line-cell={`${rowIndex}:${ci}`}
                              onKeyDown={(e) => handleCellKeyDown(e, rowIndex, ci)}
                              style={{ ...inputStyle, width: "100%" }}
                            />
                          );
                        case "description":
                          return (
                            <input
                              type="text"
                              value={l.description}
                              disabled={!editable}
                              onChange={(e) => update(l._key, { description: e.target.value })}
                              data-line-cell={`${rowIndex}:${ci}`}
                              onKeyDown={(e) => handleCellKeyDown(e, rowIndex, ci)}
                              style={{ ...inputStyle, width: "100%", minWidth: 120 }}
                              placeholder="種別・内容"
                            />
                          );
                        case "fee":
                          return (
                            <input
                              type="number"
                              min={0}
                              value={l.fee_amount}
                              disabled={!editable}
                              onChange={(e) => update(l._key, { fee_amount: Math.floor(Number(e.target.value) || 0) })}
                              data-line-cell={`${rowIndex}:${ci}`}
                              onKeyDown={(e) => handleCellKeyDown(e, rowIndex, ci)}
                              style={{ ...inputStyle, width: 120, textAlign: "right" }}
                            />
                          );
                        case "tax":
                          return (
                            <input
                              type="number"
                              min={0}
                              value={l.tax_amount}
                              disabled={!editable}
                              onChange={(e) => update(l._key, { tax_amount: Math.floor(Number(e.target.value) || 0) })}
                              data-line-cell={`${rowIndex}:${ci}`}
                              onKeyDown={(e) => handleCellKeyDown(e, rowIndex, ci)}
                              style={{ ...inputStyle, width: 120, textAlign: "right" }}
                            />
                          );
                        default:
                          // 計算式列・件数・加減率など — 入力不可（読み取り専用プレースホルダー）
                          return (
                            <span style={{
                              display: "block",
                              textAlign: col.format === "number" || col.format === "percent" ? "right" : "left",
                              color: "#94a3b8",
                              fontSize: 12,
                              userSelect: "none",
                            }}>—</span>
                          );
                      }
                    })();

                    return (
                      <td key={col.id} style={{ ...tdStyle, textAlign: isRight ? "right" : "left" }}>
                        {cellContent}
                      </td>
                    );
                  })}
                  <td style={{ ...tdStyle, width: 28 }}>
                    {editable && lines.length > 1 && (
                      <button
                        onClick={() => removeRow(l._key)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 16, padding: "0 4px" }}
                      >×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {editable && (
          <button type="button" onClick={addRow} style={{ ...btnStyle("ghost"), marginTop: 8, fontSize: 13 }}>
            + 行を追加
          </button>
        )}
      </div>
    );
  }

  // ─── フォールバック: テンプレートなし / 全列が none ───────────────────
  return (
    <div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
            {isEstimate && <th style={thStyle}>業種</th>}
            <th style={thStyle}>日付</th>
            <th style={{ ...thStyle, width: "40%" }}>内容</th>
            <th style={{ ...thStyle, textAlign: "right" }}>報酬額</th>
            <th style={{ ...thStyle, textAlign: "right" }}>登録税等</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, rowIndex) => (
            <tr key={l._key} style={{ borderBottom: "1px solid #f1f5f9" }}>
              {isEstimate && (
                <td style={tdStyle}>
                  <select
                    value={l.profession ?? ""}
                    disabled={!editable}
                    onChange={(e) => update(l._key, { profession: e.target.value as LineRow["profession"] || null })}
                    data-line-cell={`${rowIndex}:0`}
                    onKeyDown={(e) => handleCellKeyDown(e, rowIndex, 0)}
                    style={inputStyle}
                  >
                    {renderProfessionOptions(l.profession)}
                  </select>
                </td>
              )}
              <td style={tdStyle}>
                <input
                  type="date"
                  value={l.item_date ?? ""}
                  disabled={!editable}
                  onChange={(e) => update(l._key, { item_date: e.target.value || undefined })}
                  data-line-cell={`${rowIndex}:${isEstimate ? 1 : 0}`}
                  onKeyDown={(e) => handleCellKeyDown(e, rowIndex, isEstimate ? 1 : 0)}
                  style={{ ...inputStyle, width: 130 }}
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={l.description}
                  disabled={!editable}
                  onChange={(e) => update(l._key, { description: e.target.value })}
                  data-line-cell={`${rowIndex}:${isEstimate ? 2 : 1}`}
                  onKeyDown={(e) => handleCellKeyDown(e, rowIndex, isEstimate ? 2 : 1)}
                  style={{ ...inputStyle, width: "100%" }}
                  placeholder="種別・内容"
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="number"
                  min={0}
                  value={l.fee_amount}
                  disabled={!editable}
                  onChange={(e) => update(l._key, { fee_amount: Math.floor(Number(e.target.value) || 0) })}
                  data-line-cell={`${rowIndex}:${isEstimate ? 3 : 2}`}
                  onKeyDown={(e) => handleCellKeyDown(e, rowIndex, isEstimate ? 3 : 2)}
                  style={{ ...inputStyle, width: 110, textAlign: "right" }}
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="number"
                  min={0}
                  value={l.tax_amount}
                  disabled={!editable}
                  onChange={(e) => update(l._key, { tax_amount: Math.floor(Number(e.target.value) || 0) })}
                  data-line-cell={`${rowIndex}:${isEstimate ? 4 : 3}`}
                  onKeyDown={(e) => handleCellKeyDown(e, rowIndex, isEstimate ? 4 : 3)}
                  style={{ ...inputStyle, width: 110, textAlign: "right" }}
                />
              </td>
              <td style={tdStyle}>
                {editable && lines.length > 1 && (
                  <button
                    onClick={() => removeRow(l._key)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 16, padding: "0 4px" }}
                  >×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editable && (
        <button type="button" onClick={addRow} style={{ ...btnStyle("ghost"), marginTop: 8, fontSize: 13 }}>
          + 行を追加
        </button>
      )}
    </div>
  );
}

function TemplateGridSummary({ grid, data }: { grid: GridBlock; data: TemplateData }) {
  if (!grid.summary?.length) return null;
  const lastCurrencyColumn =
    [...grid.columns].reverse().find((column) => column.format === "currency") ?? grid.columns.at(-1);

  return (
    <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
      <table style={{ minWidth: 280, borderCollapse: "collapse", fontSize: 13 }}>
        <tbody>
          {grid.summary.map((row) => {
            const rawValue = lastCurrencyColumn ? row.cells[lastCurrencyColumn.id] : Object.values(row.cells)[0];
            const value = evaluateSummaryCell(rawValue, data);
            return (
              <tr key={row.id}>
                <td style={{ padding: "6px 10px", borderBottom: "1px solid #e2e8f0", color: "#475569", fontWeight: row.emphasis ? 800 : 600 }}>
                  {row.ref ? `${row.ref} ` : ""}{row.label}
                </td>
                <td style={{ padding: "6px 10px", borderBottom: "1px solid #e2e8f0", textAlign: "right", color: row.emphasis ? "#0f766e" : "#0f172a", fontWeight: row.emphasis ? 900 : 700 }}>
                  {formatTemplateValue(value, lastCurrencyColumn?.format)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type TemplateDisplayRow =
  | { kind: "template"; row: GridBlock["rows"][number] }
  | { kind: "line"; row: GridBlock["rows"][number]; line: LineRow; lineIndex: number }
  | { kind: "subtotal"; row: GridBlock["rows"][number] };

function isDetailTemplateRow(row: GridBlock["rows"][number]): boolean {
  return row.type !== "subtotal" && !row.is_section && !row.is_subsection && Boolean(row.editable?.length || row.cells);
}

function cloneDetailRow(row: GridBlock["rows"][number], index: number): GridBlock["rows"][number] {
  if (index === 0) return row;
  return {
    ...row,
    id: `${row.id}_line_${index + 1}`,
    label: row.label ? `${row.label}-${index + 1}` : undefined,
  };
}

function buildTemplateDisplayRows(grid: GridBlock, lines: LineRow[]): TemplateDisplayRow[] {
  const detailRows = grid.rows.filter(isDetailTemplateRow);
  const firstDetail = detailRows[0] ?? null;
  const output: TemplateDisplayRow[] = [];
  let lineCursor = 0;

  for (const row of grid.rows) {
    if (isDetailTemplateRow(row)) {
      const line = lines[lineCursor];
      if (line) {
        output.push({ kind: "line", row, line, lineIndex: lineCursor });
        lineCursor += 1;
      }
      continue;
    }
    output.push({ kind: row.type === "subtotal" ? "subtotal" : "template", row });
  }

  while (firstDetail && lineCursor < lines.length) {
    output.splice(Math.max(output.length - 1, 0), 0, {
      kind: "line",
      row: cloneDetailRow(firstDetail, lineCursor),
      line: lines[lineCursor]!,
      lineIndex: lineCursor,
    });
    lineCursor += 1;
  }

  return output;
}

function templateColumnRole(column: GridColumnDef): CellRole | "quantity" | "rate" | "note" {
  const text = `${column.id} ${column.header}`.toLowerCase();
  if (/qty|quantity|件数|数量/.test(text)) return "quantity";
  if (/rate|加減率|割合/.test(text)) return "rate";
  if (/note|摘要|備考/.test(text)) return "note";
  if (/登録税|印紙|立替|実費|郵送|送料|pass_through|tax_amount/.test(text)) return "tax";
  if (/amount|計|報酬|fee|金額|base|基準/.test(text) && column.format === "currency") return "fee";
  if (/date|日付|年月日/.test(text)) return "date";
  if (/\bprofession\b|\bprof_|\b業種\b/.test(text)) return "profession";
  return "description";
}

function formatGridCell(value: unknown, format?: string): string {
  if (value === null || value === undefined || value === "") return "";
  if (format === "currency") return formatJPY(Number(value) || 0);
  if (format === "percent") return `${Math.round((Number(value) || 0) * 1000) / 10}%`;
  return String(value);
}

function TemplateDocumentGrid({
  grid, lines, docType, onChange, data, editable,
}: {
  grid: GridBlock;
  lines: LineRow[];
  docType: string;
  onChange: (lines: LineRow[]) => void;
  data: TemplateData;
  editable: boolean;
}) {
  // Phase 3（document-type-master-phase3.md §1.9）: 資格ラベル表示を
  // tenant_professions から動的に組み立てる（既存3資格固定の PROFESSION_LABEL を撤去）。
  const professionsQuery = useQuery({
    queryKey: ["tenant-professions"],
    queryFn: () => tenantProfessionsApi.list(),
  });
  const professionLabel = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of professionsQuery.data?.data ?? []) map[p.profession] = p.qualification_label;
    return map;
  }, [professionsQuery.data]);

  const rows = buildTemplateDisplayRows(grid, lines);
  const totalWidth = grid.columns.reduce((sum, column) => sum + column.width, 0);

  function updateLine(lineIndex: number, patch: Partial<LineRow>) {
    onChange(lines.map((line, index) => index === lineIndex ? { ...line, ...patch } : line));
  }

  function addRow() {
    const maxOrder = Math.max(0, ...lines.map((line) => line.line_order));
    onChange([...lines, { _key: Date.now(), line_order: maxOrder + 1, description: "", fee_amount: 0, tax_amount: 0, profession: docType === "estimate" ? null : "judicial" }]);
  }

  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", minWidth: Math.max(520, totalWidth), borderCollapse: "collapse", tableLayout: "fixed", fontSize: 12 }}>
          <thead>
            <tr>
              {grid.columns.map((column) => (
                <th
                  key={column.id}
                  style={{
                    width: column.width,
                    padding: "7px 8px",
                    border: "1px solid #cbd5e1",
                    background: "#f1f5f9",
                    color: "#334155",
                    textAlign: column.format === "currency" || column.format === "number" || column.format === "percent" ? "right" : "left",
                    fontWeight: 800,
                  }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((entry) => {
              const row = entry.row;
              const key = entry.kind === "line" ? `${row.id}_${entry.line._key}` : row.id;
              const rowBg = row.is_section || row.is_subsection ? "#f8fafc" : entry.kind === "subtotal" ? "#f0fdfa" : "#fff";
              return (
                <tr key={key} style={{ background: rowBg }}>
                  {grid.columns.map((column, columnIndex) => {
                    const role = templateColumnRole(column);
                    const isRight = column.format === "currency" || column.format === "number" || column.format === "percent";
                    const isLabelColumn = columnIndex === 0;

                    if (entry.kind !== "line") {
                      const subtotalValue = role === "fee" && entry.kind === "subtotal" ? getPath(data, "detail.subtotal") : row.cells?.[column.id];
                      return (
                        <td
                          key={column.id}
                          style={{
                            padding: "7px 8px",
                            border: "1px solid #e2e8f0",
                            textAlign: isRight ? "right" : "left",
                            fontWeight: row.is_section || row.type === "subtotal" ? 800 : 700,
                            color: "#334155",
                          }}
                        >
                          {isLabelColumn ? row.label : formatGridCell(subtotalValue, column.format)}
                        </td>
                      );
                    }

                    const line = entry.line;
                    const value =
                      role === "description" ? line.description :
                      role === "quantity" ? 1 :
                      role === "rate" ? 0 :
                      role === "fee" ? line.fee_amount :
                      role === "tax" ? line.tax_amount :
                      role === "date" ? line.item_date ?? "" :
                      role === "profession" ? (line.profession ? (professionLabel[line.profession] ?? line.profession) : "") :
                      "";
                    const canEdit = editable && (role === "description" || role === "fee" || role === "tax" || role === "date" || role === "profession");

                    return (
                      <td key={column.id} style={{ padding: "6px 8px", border: "1px solid #e2e8f0", textAlign: isRight ? "right" : "left" }}>
                        {canEdit && role === "description" ? (
                          <input
                            value={line.description}
                            onChange={(event) => updateLine(entry.lineIndex, { description: event.target.value })}
                            style={{ ...inputStyle, width: "100%", minWidth: 120 }}
                          />
                        ) : canEdit && role === "fee" ? (
                          <input
                            type="number"
                            min={0}
                            value={line.fee_amount}
                            onChange={(event) => updateLine(entry.lineIndex, { fee_amount: Math.floor(Number(event.target.value) || 0) })}
                            style={{ ...inputStyle, width: 100, textAlign: "right" }}
                          />
                        ) : canEdit && role === "tax" ? (
                          <input
                            type="number"
                            min={0}
                            value={line.tax_amount}
                            onChange={(event) => updateLine(entry.lineIndex, { tax_amount: Math.floor(Number(event.target.value) || 0) })}
                            style={{ ...inputStyle, width: 100, textAlign: "right" }}
                          />
                        ) : canEdit && role === "date" ? (
                          <input
                            type="date"
                            value={line.item_date ?? ""}
                            onChange={(event) => updateLine(entry.lineIndex, { item_date: event.target.value || undefined })}
                            style={{ ...inputStyle, width: 130 }}
                          />
                        ) : (
                          <span>{formatGridCell(value, column.format)}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editable && (
        <button type="button" onClick={addRow} style={{ ...btnStyle("ghost"), marginTop: 8, fontSize: 13 }}>
          + 行を追加
        </button>
      )}
      <TemplateGridSummary grid={grid} data={data} />
    </div>
  );
}

// ── ドキュメント用ブロック追加ヘルパー ──────────────────────────────────────
type DocBlockKind = "title" | "office" | "recipient" | "issuer_name" | "issuer_qualification"
  | "invoice_registration" | "transactionDate" | "text" | "spacer" | "divider";

const DOC_BLOCK_ADD_ITEMS: { kind: DocBlockKind; label: string }[] = [
  { kind: "title",                label: "タイトル" },
  { kind: "recipient",            label: "宛名" },
  { kind: "office",               label: "会社名" },
  { kind: "issuer_name",          label: "担当者名" },
  { kind: "issuer_qualification", label: "資格名" },
  { kind: "invoice_registration", label: "適格番号" },
  { kind: "transactionDate",      label: "取引年月" },
  { kind: "text",                 label: "テキスト" },
  { kind: "spacer",               label: "余白" },
  { kind: "divider",              label: "区切り線" },
];

function createDocBlock(kind: DocBlockKind): AnyBlock {
  const suffix = Date.now().toString(36);
  switch (kind) {
    case "title":
      return { id: `blk_title_${suffix}`, type: "title", text: "タイトル", style: { font_size: 20, align: "center" } };
    case "recipient":
      return { id: `blk_recipient_${suffix}`, label: "宛名", type: "kv_block", orientation: "horizontal",
        items: [{ label: "宛名", value: "", data_key: "client.name", input_type: "text" }] };
    case "office":
      return { id: `blk_office_${suffix}`, label: "会社名", type: "kv_block", orientation: "horizontal",
        items: [{ label: "会社名", value: "登記測量 長坂・天野合同事務所", input_type: "text" }] };
    case "issuer_name":
      return { id: `blk_issuer_name_${suffix}`, label: "担当者名", type: "kv_block", orientation: "horizontal",
        items: [{ label: "担当者", value: "", data_key: "issuer.name", input_type: "text" }] };
    case "issuer_qualification":
      return { id: `blk_issuer_qual_${suffix}`, label: "資格名", type: "kv_block", orientation: "horizontal",
        items: [{ label: "資格名", value: "", data_key: "issuer.qualification", input_type: "text" }] };
    case "invoice_registration":
      return { id: `blk_invreg_${suffix}`, label: "適格番号", type: "kv_block", orientation: "horizontal",
        items: [{ label: "適格請求書番号", value: "", data_key: "invoice.registration_number", input_type: "text" }] };
    case "transactionDate":
      return { id: `blk_txdate_${suffix}`, label: "取引年月", type: "kv_block", orientation: "horizontal",
        items: [{ label: "取引年月", value: "", data_key: "transaction.date", input_type: "date" }] };
    case "text":
      return { id: `blk_text_${suffix}`, type: "text", text: "" };
    case "spacer":
      return { id: `blk_spacer_${suffix}`, type: "spacer", height: 16 };
    case "divider":
      return { id: `blk_divider_${suffix}`, type: "divider" };
  }
}

// ── 書類用ブロックパネル（左サイドバー）────────────────────────────────────
function DocBlockPanel({
  blocks, selectedBlockId, onSelect, onAdd, onMove, onDelete,
}: {
  blocks:          AnyBlock[];
  selectedBlockId: string | null;
  onSelect:        (id: string) => void;
  onAdd:           (kind: DocBlockKind) => void;
  onMove:          (id: string, dir: "up" | "down") => void;
  onDelete:        (id: string) => void;
}) {
  const [addOpen, setAddOpen] = useState(false);
  return (
    <aside style={{ width: 200, flexShrink: 0, borderRight: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", flexDirection: "column", maxHeight: 600, overflowY: "auto" }}>
      <div style={{ padding: "10px 10px 6px", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6 }}>ブロック構成</div>
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setAddOpen((v) => !v)}
            style={{ ...btnStyle("outline"), width: "100%", fontSize: 12, padding: "5px 8px" }}
          >
            + ブロックを追加
          </button>
          {addOpen && (
            <div style={{
              position: "absolute", top: "100%", left: 0, zIndex: 50, marginTop: 4,
              background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,.12)",
              width: 180, padding: 6,
            }}>
              {DOC_BLOCK_ADD_ITEMS.map((item) => (
                <button
                  key={item.kind}
                  onClick={() => { onAdd(item.kind); setAddOpen(false); }}
                  style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", padding: "6px 10px", fontSize: 13, borderRadius: 5, color: "#0f172a" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#f0fdfa"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div style={{ padding: 8, flex: 1 }}>
        {blocks.map((block, idx) => {
          const isGrid = block.type === "grid";
          const label = block.label ?? (block.type === "title" ? "タイトル" : block.type === "text" ? "テキスト" : block.type === "spacer" ? "余白" : block.type === "divider" ? "区切り線" : block.id);
          const isSelected = selectedBlockId === block.id;
          return (
            <div key={block.id} style={{ marginBottom: 4, border: `1px solid ${isSelected ? "#0f766e" : "#e2e8f0"}`, borderRadius: 7, background: isSelected ? "#f0fdfa" : "#fff", overflow: "hidden" }}>
              <button
                onClick={() => onSelect(block.id)}
                style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", padding: "7px 10px 4px", fontFamily: "inherit" }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{label}</div>
                <div style={{ fontSize: 10, color: "#94a3b8" }}>{block.type}{isGrid ? " （固定）" : ""}</div>
              </button>
              {!isGrid && (
                <div style={{ display: "flex", gap: 3, padding: "0 8px 6px" }}>
                  <button disabled={idx === 0} onClick={() => onMove(block.id, "up")}
                    style={{ flex: 1, fontSize: 11, padding: "3px 0", background: idx === 0 ? "#f1f5f9" : "#e2e8f0", border: "none", borderRadius: 4, cursor: idx === 0 ? "default" : "pointer", color: "#475569" }}>↑</button>
                  <button disabled={idx === blocks.length - 1} onClick={() => onMove(block.id, "down")}
                    style={{ flex: 1, fontSize: 11, padding: "3px 0", background: idx === blocks.length - 1 ? "#f1f5f9" : "#e2e8f0", border: "none", borderRadius: 4, cursor: idx === blocks.length - 1 ? "default" : "pointer", color: "#475569" }}>↓</button>
                  <button onClick={() => { if (confirm(`「${label}」を削除しますか？`)) onDelete(block.id); }}
                    style={{ flex: 1, fontSize: 11, padding: "3px 0", background: "#fee2e2", border: "none", borderRadius: 4, cursor: "pointer", color: "#dc2626" }}>削除</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

// ── テンプレートのブロック順に編集画面を描画 ─────────────────────────────────
function TemplateBlocksEditor({
  blocks, lines, docType, onChange, data, editable = true, onBlockChange, selectedBlockId,
}: {
  blocks:           AnyBlock[];
  lines:            LineRow[];
  docType:          string;
  onChange:         (lines: LineRow[]) => void;
  data:             TemplateData;
  editable?:        boolean;
  onBlockChange?:   (blockId: string, updated: AnyBlock) => void;
  selectedBlockId?: string | null;
}) {
  let gridSeen = 0;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {blocks.map((block) => {
        const isSelected = selectedBlockId === block.id;
        const blockHighlight: React.CSSProperties = isSelected && onBlockChange
          ? { outline: "2px solid #0f766e", outlineOffset: 2, borderRadius: 4 }
          : {};

        switch (block.type) {
          case "title": {
            const align = block.style?.align ?? "center";
            const size  = block.style?.font_size ?? 20;
            if (editable && onBlockChange) {
              return (
                <div key={block.id} style={{ margin: "4px 0 10px", ...blockHighlight }}>
                  <input
                    value={block.text}
                    onChange={(e) => onBlockChange(block.id, { ...block, text: e.target.value })}
                    style={{ width: "100%", textAlign: align, fontSize: size, fontWeight: 800, color: "#0f172a", border: "1px dashed #cbd5e1", borderRadius: 4, padding: "2px 6px", background: "#fafafa", boxSizing: "border-box" }}
                  />
                </div>
              );
            }
            return (
              <div key={block.id} style={{ textAlign: align, fontSize: size, fontWeight: 800, color: "#0f172a", margin: "4px 0 10px" }}>
                {interpolateTemplateValue(block.text, data)}
              </div>
            );
          }
          case "text": {
            const align = block.style?.align ?? "left";
            const size  = block.style?.font_size ?? 12;
            if (editable && onBlockChange) {
              return (
                <div key={block.id} style={{ margin: "4px 0", ...blockHighlight }}>
                  <textarea
                    value={block.text}
                    onChange={(e) => onBlockChange(block.id, { ...block, text: e.target.value })}
                    rows={3}
                    style={{ width: "100%", textAlign: align, fontSize: size, color: "#475569", border: "1px dashed #cbd5e1", borderRadius: 4, padding: "4px 6px", background: "#fafafa", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }}
                  />
                </div>
              );
            }
            return (
              <div key={block.id} style={{ textAlign: align, fontSize: size, color: "#475569", whiteSpace: "pre-wrap", margin: "4px 0" }}>
                {interpolateTemplateValue(block.text, data)}
              </div>
            );
          }
          case "spacer":
            return <div key={block.id} style={{ height: block.height }} />;
          case "divider":
            return <hr key={block.id} style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "10px 0" }} />;
          case "kv_block": {
            if (editable && onBlockChange) {
              return (
                <div key={block.id} style={{ display: "flex", flexDirection: "column", gap: 6, margin: "6px 0", fontSize: 12, ...blockHighlight }}>
                  {block.items.map((it, i) => {
                    const currentVal = it.data_key
                      ? String(getPath(data, it.data_key) ?? it.value ?? "")
                      : String(it.value ?? "");
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontWeight: 600, color: "#64748b", minWidth: 80 }}>{it.label}</span>
                        <input
                          type={it.input_type === "date" ? "date" : "text"}
                          value={currentVal}
                          onChange={(e) => {
                            const newItems = block.items.map((item, idx) =>
                              idx === i ? { ...item, value: e.target.value, data_key: undefined } : item
                            );
                            onBlockChange(block.id, { ...block, items: newItems });
                          }}
                          style={{ flex: 1, ...inputStyle }}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            }
            return (
              <div key={block.id} style={{ display: "flex", flexDirection: "column", gap: 3, margin: "6px 0", fontSize: 12, color: "#64748b" }}>
                {block.items.map((it, i) => (
                  <div key={i} style={{ display: "flex", gap: 6 }}>
                    <span style={{ fontWeight: 600 }}>{it.label}</span>
                    <span style={{ color: "#475569" }}>
                      {formatTemplateValue(
                        it.data_key ? getPath(data, it.data_key) : interpolateTemplateValue(it.value, data),
                        it.input_type === "number" ? "number" : undefined,
                      ) || "—"}
                    </span>
                  </div>
                ))}
              </div>
            );
          }
          case "computed_value":
            return (
              <div key={block.id} style={{ textAlign: block.style?.align ?? "right", fontSize: block.style?.font_size ?? 12, color: "#0f172a", fontWeight: block.style?.bold ? 800 : 600, margin: "6px 0" }}>
                {block.label} {formatTemplateValue(computedBlockValue(block, data), block.format === "currency" ? "currency" : "number")}
              </div>
            );
          case "grid": {
            gridSeen += 1;
            const isPrimaryGrid = gridSeen === 1;
            if (isPrimaryGrid) {
              return (
                <div key={block.id} style={{ margin: "6px 0 12px" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>
                    {block.label ?? "明細"}
                  </div>
                  <TemplateDocumentGrid
                    grid={block}
                    lines={lines}
                    docType={docType}
                    onChange={onChange}
                    data={data}
                    editable={editable}
                  />
                </div>
              );
            }
            return (
              <div key={block.id} style={{
                margin: "6px 0 12px", padding: "8px 12px",
                background: "#f8fafc", border: "1px dashed #cbd5e1",
                borderRadius: 6, fontSize: 12, color: "#94a3b8",
              }}>
                {block.label ?? "明細"}：上の明細と同じ内容がここにも表示されます
              </div>
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
}

const thStyle: React.CSSProperties = { padding: "8px 10px", textAlign: "left", color: "#64748b", fontWeight: 600 };
const tdStyle: React.CSSProperties = { padding: "6px 10px" };
const inputStyle: React.CSSProperties = { padding: "5px 8px", border: "1px solid #cbd5e1", borderRadius: 5, fontSize: 13 };

// ── PDF.js Canvas レンダラー ──────────────────────────────────────────────────
function PdfViewer({ url }: { url: string }) {
  const [dataUrls, setDataUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setDataUrls([]);

    import("pdfjs-dist").then(async (lib) => {
      // Vite でビルドされた Worker URL を参照
      const workerUrl = new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url).href;
      lib.GlobalWorkerOptions.workerSrc = workerUrl;

      const pdf = await lib.getDocument({ url, disableStream: false }).promise;
      const urls: string[] = [];
      for (let p = 1; p <= pdf.numPages; p++) {
        if (cancelled) return;
        const page = await pdf.getPage(p);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width  = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) continue;
        await page.render({ canvas, canvasContext: ctx, viewport }).promise;
        urls.push(canvas.toDataURL("image/png"));
      }
      if (!cancelled) { setDataUrls(urls); setLoading(false); }
    }).catch((e: unknown) => {
      if (!cancelled) { setErr(String(e)); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, [url]);

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 13 }}>PDFを描画中…</div>
  );
  if (err) return (
    <div style={{ padding: 40, textAlign: "center", color: "#dc2626", fontSize: 13 }}>描画エラー: {err}</div>
  );
  return (
    <div style={{ background: "#525659", padding: "16px 0", overflowY: "auto", maxHeight: 820 }}>
      {dataUrls.map((src, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
          <img src={src} alt={`${i + 1}ページ`} style={{ maxWidth: "100%", boxShadow: "0 2px 12px rgba(0,0,0,.4)" }} />
        </div>
      ))}
    </div>
  );
}

// ── DocumentPage ────────────────────────────────────────────────────────────
export function DocumentPage() {
  useScreenView("document");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [lines, setLines] = useState<LineRow[]>([]);
  const [blocksDraft, setBlocksDraft] = useState<AnyBlock[] | null>(null);
  const [selectedDocBlockId, setSelectedDocBlockId] = useState<string | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPreviewBlob, setPdfPreviewBlob] = useState<Blob | null>(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState<string | null>(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  // 発行直後に PDF プレビュー（ダウンロード導線）へ自動スクロールするためのアンカー
  const pdfPreviewRef = useRef<HTMLDivElement | null>(null);

  // pdfPreviewUrl は発行フロー（saveAndIssueMut）でのみセットされる。
  // 発行完了＝プレビュー表示のタイミングでダウンロード画面（プレビューカード）まで遷移する。
  useEffect(() => {
    if (pdfPreviewUrl && !pdfPreviewLoading) {
      pdfPreviewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [pdfPreviewUrl, pdfPreviewLoading]);
  const [simpleTemplateOpen, setSimpleTemplateOpen] = useState(false);
  const [saveAsTemplateModal, setSaveAsTemplateModal] = useState(false);
  const [saveAsTemplateName, setSaveAsTemplateName] = useState("");
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [reviseModal, setReviseModal] = useState(false);
  const [reviseReason, setReviseReason] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [justIssued, setJustIssued] = useState(false);
  const [payForm, setPayForm] = useState<{ paid_date: string; bank_name: string; amount: string; memo: string } | null>(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState<number>(0);
  // M1: 宛名・請求日・備考（書類ごとの内容フィールド）
  const [recipient, setRecipient] = useState<string>("");
  const [issueDate, setIssueDate] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  // 発行者の書類ごとの上書き（null = tenant_professions の既定資格者）
  const [issuerHolderId, setIssuerHolderId] = useState<string | null>(null);
  // 料金表ベースの書類（土地/建物計算書・保証協会請求書）の構造化計算入力
  const [calcInput, setCalcInput] = useState<FeeCalcInput | null>(null);
  // document-create-flow §6-2: 請求書発行成功後の見積書案内モーダル（発行のたびに1回だけ）
  const [showEstimateGuide, setShowEstimateGuide] = useState(false);
  // document-create-flow §6-4(b): モーダルの表示理由でタイトルを切り替える
  //   "issued"  = 発行成功後（既定）／"prompt" = 作成直後の promptEstimate 誘導
  const [estimateGuideReason, setEstimateGuideReason] = useState<"issued" | "prompt">("issued");

  const { data: doc, isLoading } = useQuery({
    queryKey: ["document", id],
    queryFn: () => documentsApi.get(id!),
    enabled: !!id,
  });

  const { data: caseDetail } = useQuery({
    queryKey: ["case", doc?.case_id],
    queryFn: () => casesApi.get(doc!.case_id),
    enabled: !!doc?.case_id,
  });

  // ── 一括作成の「仕上げキュー」 ─────────────────────────────────────────────
  // 実施業務からまとめて作った下書きを1件ずつ巡回するためのナビ。
  // sessionStorage 由来でリアクティブではないので、閉じた時だけ読み直す。
  const [queueVersion, setQueueVersion] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const docQueue = useMemo(() => getDocQueue(doc?.case_id), [doc?.case_id, queueVersion]);
  // キューが張られていても、キュー外の書類を開いている間はナビを出さない
  const inQueue = queuePosition(docQueue, id) !== null;

  const queueEntries = useMemo(() => {
    if (!docQueue || !inQueue) return null;
    const byId = new Map((caseDetail?.documents ?? []).map((d) => [d.id, d]));
    return docQueue.docIds.map((docId) => {
      const summary = byId.get(docId);
      return {
        id:        docId,
        doc:       summary,
        // 取消・削除などで見つからない書類は「進めない」ので完了扱いにして巡回から外す
        done:      summary ? summary.status !== "draft" : true,
        isCurrent: docId === id,
      };
    });
  }, [docQueue, inQueue, caseDetail?.documents, id]);

  // 次に仕上げるべき書類（現在の書類は除く）。発行直後の遷移先にも使う
  const queueNextPendingId = useMemo(() => {
    if (!queueEntries) return null;
    const doneById = new Map(queueEntries.map((e) => [e.id, e.done]));
    return nextPendingDocId(docQueue, id, (docId) => doneById.get(docId) ?? true);
  }, [docQueue, queueEntries, id]);

  function dismissQueue() {
    if (doc?.case_id) clearDocQueue(doc.case_id);
    setQueueVersion((v) => v + 1);
  }

  // 料金表ベースの書類のみ: テナントの料金表（カスタム行が無ければ既定値）を取得
  const feeDocType = doc && isFeeScheduleDocType(doc.doc_type) ? doc.doc_type : null;
  const {
    data: feeScheduleRes,
    isError: feeScheduleError,
    refetch: refetchFeeSchedule,
  } = useQuery({
    // FeeSchedulePage（/settings/fee-schedules）が保存時に ["fee-schedules"] を
    // invalidate するので、ここも同じ接頭辞にして保存直後にドラフトへ反映されるようにする
    queryKey: ["fee-schedules", feeDocType],
    queryFn: () => feeSchedulesApi.get(feeDocType!),
    enabled: !!feeDocType,
    staleTime: 30_000,
  });
  const feeSchedule = feeScheduleRes?.schedule ?? null;

  // estimate-invoice-sync §3.4: 見積書 draft のとき、請求書集約との同期状態を取得
  const isEstimateDraftQuery = doc?.doc_type === "estimate" && doc?.status === "draft";
  // document-create-flow §6-2: 請求書のとき、発行後の見積書案内モーダルに使う同期状態も同じキーで取得
  // （EstimateSyncCard 内の useQuery ともキー共有。isInvoiceDoc は doc 確定後にしか使えないためここでは同義の判定を直書き）
  const isInvoiceDocQuery = doc ? doc.doc_type.endsWith("_invoice") && doc.doc_type !== "guarantee_invoice" : false;
  const { data: estimateStatus } = useQuery({
    queryKey: ["case", doc?.case_id, "estimate-status"],
    queryFn: () => casesApi.estimateStatus(doc!.case_id),
    enabled: !!doc?.case_id && (isEstimateDraftQuery || isInvoiceDocQuery),
  });

  // document-create-flow §6-4(b): 作成直後に「見積書を作らず請求書1通だけ」navigate してきた場合、
  // estimateStatus.sync === "not_created" なら §6-2 の案内モーダルを「見積書も作成しますか？」で1度だけ表示する。
  // 読み取り後は history state を消してリロード時に再表示しないようにする。
  useEffect(() => {
    const state = location.state as { promptEstimate?: boolean; fromDocumentId?: string } | null;
    if (!state?.promptEstimate) return;
    if (!doc || estimateStatus === undefined) return; // 両方揃うまで待つ（state はまだ消さない）
    if (estimateStatus.sync === "not_created") {
      setEstimateGuideReason("prompt");
      setShowEstimateGuide(true);
    }
    // fromDocumentId（請求書に戻る導線）は残したまま promptEstimate だけ消す
    navigate(location.pathname, {
      replace: true,
      state: state.fromDocumentId ? { fromDocumentId: state.fromDocumentId } : null,
    });
  }, [location.state, location.pathname, doc, estimateStatus, navigate]);

  // 請求書画面の「見積書を開く」経由で来た場合、元の請求書へ戻るボタンを出す
  const fromDocumentId = (location.state as { fromDocumentId?: string } | null)?.fromDocumentId;

  // document-create-flow §6-2: 発行後の見積書案内モーダルから「作成」「更新」を実行 → 見積書へ遷移
  // invalidate / エラーハンドリングは EstimateSyncCard を踏襲
  const estimateGuideMut = useMutation({
    mutationFn: (mode: "create" | "refresh") => casesApi.estimateGenerate(doc!.case_id, mode),
    onSuccess: (estimateDoc) => {
      qc.invalidateQueries({ queryKey: ["case", doc!.case_id] });
      qc.invalidateQueries({ queryKey: ["case", doc!.case_id, "estimate-status"] });
      qc.invalidateQueries({ queryKey: ["document", estimateDoc.id] });
      setShowEstimateGuide(false);
      // 見積書側に「請求書に戻る」導線を出すため、遷移元の請求書IDを渡す
      navigate(`/documents/${estimateDoc.id}`, { state: { fromDocumentId: doc!.id } });
    },
    onError: (e: EstimateApiError) => {
      // レース（他タブでの並行作成 等）でも表示を最新状態に戻す
      if (doc?.case_id) {
        qc.invalidateQueries({ queryKey: ["case", doc.case_id] });
        qc.invalidateQueries({ queryKey: ["case", doc.case_id, "estimate-status"] });
      }
      if (e.body?.error?.code === "ESTIMATE_DRAFT_EXISTS") {
        toast("既存の下書きがあります。表示を最新の状態に更新しました。", "error");
      } else {
        toast(e.message, "error");
      }
    },
  });

  // テンプレート管理で保存された当該 doc_type のテンプレートを取得。
  // 既定テンプレートが存在し、grid block を含む場合は明細の列構成をそれに合わせる。
  const { data: templatesData } = useQuery({
    queryKey: ["templates", doc?.doc_type],
    queryFn: () => templatesApi.list(doc!.doc_type),
    enabled: !!doc?.doc_type,
    // retry: false は外す。1回失敗するとテンプレートなし扱いになってしまうため
    staleTime: 30_000,
  });

  // 見本レイアウト（プリセット）が用意されている doc_type。
  // 以前は ["judicial_invoice", "estimate"] をこの画面にハードコードしていたため、
  // 行政書士請求書・調査士請求書のプリセットが存在するのに適用導線が出ていなかった。
  // サーバーの PRESETS レジストリを唯一の真実の源にする。
  const { data: presetsData } = useQuery({
    queryKey: ["template-presets"],
    queryFn: () => templatesApi.listPresets(),
    staleTime: 5 * 60_000,
  });
  const presetDocTypes = useMemo(
    () => new Set((presetsData?.data ?? []).map((p) => p.doc_type)),
    [presetsData],
  );
  // バックエンド(pdf.ts)と同じ選択ロジック：既定テンプレートを優先し、
  // 無ければ最新のテンプレート（list は updated_at DESC 順）を採用する。
  const availableTemplates = templatesData?.data ?? [];
  const templateForDoc =
    availableTemplates.find((t) => t.is_default === 1) ?? availableTemplates[0];

  // 発行者情報（業務マスタ）の未入力チェック。テンプレートが参照している値が
  // 未登録のまま発行されるのを防ぐため、draft のときだけ警告バナーを出す
  const { profession: issuerProfessionKey } = useDocTypeProfession(doc?.doc_type);
  const issuerRefs = useMemo(
    () => (templateForDoc?.template_json ? collectIssuerRefs(templateForDoc.template_json) : []),
    [templateForDoc],
  );
  const { data: issuerProfessionsData } = useQuery({
    queryKey: ["tenant-professions"],
    queryFn: () => tenantProfessionsApi.list(),
    enabled: !!issuerProfessionKey && doc?.status === "draft",
  });
  const issuerProfession = issuerProfessionsData?.data.find((entry) => entry.profession === issuerProfessionKey) ?? null;
  const missingIssuerRefs = useMemo(
    () => (issuerProfession ? issuerRefs.filter((ref) => !resolveIssuerRef(issuerProfession, ref)) : []),
    [issuerProfession, issuerRefs],
  );
  const [showIssuerDialog, setShowIssuerDialog] = useState(false);

  // 発行者の書類ごとの上書き（この書類種別に資格種別が紐づく場合のみ意味を持つ）。
  // 選択肢はその資格種別の資格者名簿に絞る（設定 → 資格の種類で管理）。
  const { data: professionHoldersData } = useQuery({
    queryKey: ["tenant-profession-holders", issuerProfessionKey],
    queryFn: () => tenantProfessionsApi.listHolders(issuerProfessionKey!),
    enabled: !!issuerProfessionKey && doc?.status === "draft",
  });
  const professionHolders = professionHoldersData?.data ?? [];
  // 既定資格者は「既定の資格者を使う」の選択肢で表現するため、一覧には別掲しない。
  // 選択肢の value は資格者名簿のid（tenant_profession_holders.id）。
  const holderOptions = professionHolders
    .filter((h) => !h.is_default)
    .map((h) => ({ id: h.id, name: h.name }));
  // 名簿が1件（既定資格者のみ）のときは選ぶ意味が無いため、選択UI自体を出さない
  // （2件以上のときだけ書類ごとの切り替えを提示する。docs/design/user-professional-separation.md §13-5）。
  const showHolderSelector = professionHolders.length >= 2;
  const selectedHolder = issuerHolderId
    ? professionHolders.find((h) => h.id === issuerHolderId) ?? null
    : null;
  // 名簿から外された（削除された）書類の上書き先を検知したら、既定の資格者に戻す。
  // 検知しないまま放置すると、select の value がどの <option> にも一致せず操作不能に見えてしまう。
  useEffect(() => {
    if (!issuerHolderId) return;
    if (!professionHoldersData) return; // まだ取得できていない
    if (selectedHolder) return;
    setIssuerHolderId(null);
    toast("選択されていた資格者は削除されているため、既定の資格者に戻しました", "error");
  }, [issuerHolderId, professionHoldersData, selectedHolder]);

  const templateGrid = pickGridBlock(templateForDoc);
  const templateBlocks = templateForDoc?.template_json?.body?.blocks ?? null;
  const templateColumns = templateGrid?.columns ?? null;
  const templateRoles = templateColumns ? assignColumnRoles(templateColumns) : null;
  const simpleSettings = templateForDoc?.template_json?.simple_settings;

  // ② 簡易設定の保存（template_json.simple_settings のみ更新）
  const saveSimpleSettingsMut = useMutation({
    mutationFn: (settings: SimpleSettings) => {
      const tpl = templateForDoc?.template_json;
      if (!templateForDoc || !tpl) throw new Error("テンプレートがありません");
      return templatesApi.update(templateForDoc.id, {
        template_json: { ...tpl, simple_settings: settings },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["templates"] });
      setSimpleTemplateOpen(false);
      toast("体裁設定を保存しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  // ドラフトは開いた時点でテンプレート面を編集可能にする。
  // 発行済みは同じ画面を読み取り専用で表示し、修正版作成へ誘導する。

  const { data: paymentsData } = useQuery({
    queryKey: ["payments", id],
    queryFn: () => paymentsApi.listByDocument(id!),
    enabled: !!id,
  });

  const addPayMut = useMutation({
    mutationFn: (body: { paid_date: string; bank_name?: string; amount: number; memo?: string }) =>
      paymentsApi.create(id!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payments", id] });
      setPayForm(null);
      toast("入金を記録しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  const deletePayMut = useMutation({
    mutationFn: (payId: string) => paymentsApi.delete(payId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["payments", id] }); toast("入金記録を削除しました"); },
    onError: (e) => toast(e.message, "error"),
  });

  // doc ロード時: 行・編集状態・スナップショット（保存済みがあれば即反映）を初期化
  useEffect(() => {
    if (!doc) return;
    setLines(toLineRows(doc.line_items));
    setAdjustmentAmount(doc.adjustment_amount ?? 0);
    const isDraftDoc = doc.status === "draft";
    setEditing(isDraftDoc);
    setPdfPreviewUrl(null);
    if (isDraftDoc && doc.blocks_snapshot) {
      setBlocksDraft(JSON.parse(doc.blocks_snapshot) as AnyBlock[]);
    } else if (!isDraftDoc) {
      setBlocksDraft(null);
    }
    // M1: 宛名・請求日・備考を DB 値（または空文字）で初期化
    setRecipient(doc.recipient_name ?? "");
    setIssueDate(doc.issue_date ?? "");
    setNotes(doc.notes ?? "");
    setIssuerHolderId(doc.issuer_holder_id ?? null);
    // 料金表ベースの書類: 保存済み calc_input を復元（未保存の旧 draft は空入力から）
    setCalcInput(isFeeScheduleDocType(doc.doc_type) ? (doc.calc_input ?? emptyFeeCalcInput(doc.doc_type)) : null);
    // blocks_snapshot がなければ Effect 2 でテンプレートから初期化される
    // NOTE: deps に updated_at を含める。見積書の自動同期（請求書保存時に
    // サーバー側で明細が差し替わる）や estimate/generate 後の refetch で
    // 明細が変わっても、id/status だけでは effect が再実行されず、
    // 画面の明細（lines state）が古いまま残るバグがあった。
  }, [doc?.id, doc?.status, doc?.updated_at]);

  // テンプレートが遅れてロードされた場合に blocksDraft を初期化（スナップショット未保存の draft のみ）
  useEffect(() => {
    if (!doc || doc.status !== "draft" || doc.blocks_snapshot || !templateBlocks) return;
    setBlocksDraft((prev) => prev !== null ? prev : [...templateBlocks]);
  }, [doc?.id, doc?.blocks_snapshot, templateBlocks]);

  function startEdit() {
    if (!doc) return;
    setLines(toLineRows(doc.line_items));
    setEditing(true);
    // スナップショットを初期化: 保存済みがあればそれを、なければ現在のテンプレートブロックをコピー
    const snapshot = doc.blocks_snapshot
      ? (JSON.parse(doc.blocks_snapshot) as AnyBlock[])
      : (templateBlocks ? [...templateBlocks] : null);
    setBlocksDraft(snapshot);
    setSelectedDocBlockId(null);
    // M1: 宛名・請求日・備考を最新 doc から再読み込み
    setRecipient(doc.recipient_name ?? "");
    setIssueDate(doc.issue_date ?? "");
    setNotes(doc.notes ?? "");
    setIssuerHolderId(doc.issuer_holder_id ?? null);
    setCalcInput(isFeeScheduleDocType(doc.doc_type) ? (doc.calc_input ?? emptyFeeCalcInput(doc.doc_type)) : null);
  }

  function handleAddBlock(kind: DocBlockKind) {
    const block = createDocBlock(kind);
    setBlocksDraft((prev) => prev ? [...prev, block] : [block]);
    setSelectedDocBlockId(block.id);
  }

  function handleMoveBlock(blockId: string, dir: "up" | "down") {
    setBlocksDraft((prev) => {
      if (!prev) return prev;
      const idx = prev.findIndex((b) => b.id === blockId);
      const next = dir === "up" ? idx - 1 : idx + 1;
      if (idx < 0 || next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next]!, arr[idx]!];
      return arr;
    });
  }

  function handleDeleteBlock(blockId: string) {
    setBlocksDraft((prev) => prev ? prev.filter((b) => b.id !== blockId) : prev);
    if (selectedDocBlockId === blockId) setSelectedDocBlockId(null);
  }

  function handleBlockChange(blockId: string, updated: AnyBlock) {
    setBlocksDraft((prev) => prev ? prev.map((b) => b.id === blockId ? updated : b) : prev);
  }

  // 保存ペイロード: 料金表ベースの書類は calc_input が正
  // （サーバーが再計算して明細を投影で全置換するため line_items は送らない）
  function buildSavePayload() {
    if (feeDocType && calcInput) {
      return {
        calc_input:     calcInput,
        notes:          notes || null,
        recipient_name: recipient || null,
        issue_date:     issueDate || null,
        issuer_holder_id: issuerHolderId,
      };
    }
    return {
      line_items: toNewLineItems(lines),
      blocks_snapshot: blocksDraft ? JSON.stringify(blocksDraft) : undefined,
      adjustment_amount: adjustmentAmount,
      // M1: 宛名・請求日・備考（空文字は null として保存 → フォールバック発動）
      notes:          notes || null,
      recipient_name: recipient || null,
      issue_date:     issueDate || null,
      issuer_holder_id: issuerHolderId,
    };
  }

  const saveMut = useMutation({
    mutationFn: () => documentsApi.update(id!, buildSavePayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["document", id] });
      // document-create-flow §6-5: 見積書の自動同期後、案件の書類一覧・同期バッジを最新化する
      if (doc?.case_id) qc.invalidateQueries({ queryKey: ["case", doc.case_id] });
      toast("保存しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  // 保存 → 発行 → PDF インラインプレビューを一括実行
  const saveAndIssueMut = useMutation({
    mutationFn: async () => {
      await documentsApi.update(id!, buildSavePayload());
      return documentsApi.issue(id!);
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ["document", id] });
      qc.invalidateQueries({ queryKey: ["case"] });
      setEditing(false);
      setBlocksDraft(null);
      toast("発行しました");

      // 発行後は単独発行・一括作成（仕上げキュー）中を問わず、常に
      // 「発行した書類のダウンロード画面」へ遷移する。仕上げキュー中に残りの書類へ
      // 進みたい場合は DocumentQueueNav の「次の書類へ →」を手動で押してもらう。
      // 連続発行しても全部に「いま発行」バッジが付くよう、発行記録は sessionStorage に積む。
      // 請求書の場合は遷移先で「続けて見積書を…」の案内モーダルを表示する（fromInvoiceIssue）。
      markIssued(doc!.case_id, id!);
      // キュー中は見積書もキューに含まれているため、重ねて見積書案内は出さない。
      const isInvoice = doc?.doc_type.endsWith("_invoice") && doc.doc_type !== "guarantee_invoice";
      navigate(`/cases/${doc!.case_id}/pdfs`, {
        state: { justIssuedId: id, fromInvoiceIssue: (isInvoice && !inQueue) || undefined },
      });
    },
    onError: (e) => toast(e.message, "error"),
  });

  const cancelMut = useMutation({
    mutationFn: () => documentsApi.cancel(id!, cancelReason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["document", id] }); setCancelModal(false); toast("取消しました"); },
    onError: (e) => toast(e.message, "error"),
  });

  const reviseMut = useMutation({
    mutationFn: () => documentsApi.revise(id!, reviseReason),
    onSuccess: (newDoc) => {
      qc.invalidateQueries({ queryKey: ["document"] });
      qc.invalidateQueries({ queryKey: ["case"] });
      setReviseModal(false);
      toast("修正版を作成しました");
      navigate(`/documents/${newDoc.id}`);
    },
    onError: (e) => toast(e.message, "error"),
  });

  // 見積書のみ: 発行後の「内容を変更する」。理由入力なしで新しい版（draft）を作り、そのまま編集へ
  const quickReviseMut = useMutation({
    mutationFn: () => documentsApi.revise(id!, "発行後の内容変更"),
    onSuccess: (newDoc) => {
      qc.invalidateQueries({ queryKey: ["document"] });
      qc.invalidateQueries({ queryKey: ["case"] });
      toast("新しい版を作成しました。内容を変更できます");
      navigate(`/documents/${newDoc.id}`);
    },
    onError: (e) => toast(e.message, "error"),
  });

  const saveAsTemplateMut = useMutation({
    mutationFn: () => {
      const sourceBlocks = blocksDraft ?? (doc?.blocks_snapshot ? JSON.parse(doc.blocks_snapshot) as AnyBlock[] : null);
      if (!sourceBlocks) throw new Error("ブロックデータがありません");
      const pageConfig = templateForDoc?.template_json?.page_config ?? {
        size: "A4" as const, orientation: "portrait" as const,
        margins: { top: 40, right: 30, bottom: 30, left: 30 },
      };
      return templatesApi.create({
        doc_type: doc!.doc_type,
        name: saveAsTemplateName || `${getDocTypeLabel(doc!.doc_type)}テンプレート`,
        is_default: false,
        schema_version: 2,
        template_json: { schema_version: 2, name: saveAsTemplateName, page_config: pageConfig, body: { blocks: sourceBlocks } },
      });
    },
    onSuccess: (tpl) => {
      qc.invalidateQueries({ queryKey: ["templates"] });
      setSaveAsTemplateModal(false);
      setSaveAsTemplateName("");
      toast("テンプレートを作成しました");
      navigate(`/settings/templates/${tpl.id}`);
    },
    onError: (e) => toast(e.message, "error"),
  });

  // プリセットを既定テンプレートとしてインストールし、blocksDraft を即時更新する
  const installPresetMut = useMutation({
    mutationFn: () => {
      if (!doc) return Promise.reject(new Error("書類データがありません"));
      return templatesApi.installPreset(doc.doc_type);
    },
    onSuccess: (newTemplate) => {
      if (!doc) return;
      qc.invalidateQueries({ queryKey: ["templates", doc.doc_type] });
      const newBlocks = newTemplate.template_json?.body?.blocks;
      if (newBlocks) setBlocksDraft([...newBlocks]);
      toast("見本の体裁を適用しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  // テンプレートが無い doc_type 向けに、空のひな型を作成してエディタを開く。
  // 作成したひな型は is_default=true で保存するので、以後この発行画面・PDF の
  // 両方でそのひな型が使われる（pdf.ts / templateForDoc の選択ロジックと一致）。
  const createTemplateMut = useMutation({
    mutationFn: () => {
      const docTypeLabel = getDocTypeLabel(doc!.doc_type);
      return templatesApi.create({
        doc_type:      doc!.doc_type,
        name:          `${docTypeLabel}ひな型`,
        is_default:    true,
        schema_version: 2,
        template_json: createBlankTemplate(`${docTypeLabel}ひな型`),
      });
    },
    onSuccess: (tpl) => {
      toast("体裁の準備ができました。詳細設定を開きます");
      navigate(`/settings/templates/${tpl.id}`);
    },
    onError: (e) => toast(e.message, "error"),
  });

  // カスタム書類種別（tenant_document_types。既存7種のいずれでもない doc_type）の
  // ライブ計算に必要な設定。既存7種は computeDocument() の switch で確定するため
  // 参照されない（document-type-master-phase2 設計書 §3.3・§6.3 と同じ「ゼロ regression」方針）。
  const { data: docTypesData } = useTenantDocumentTypes();
  const liveCalcConfig = useMemo(() => {
    const row = docTypesData?.data.find((d) => d.doc_type === doc?.doc_type);
    if (!row) return undefined;
    return { kind: row.kind === "calc_sheet" ? ("calc_sheet" as const) : ("invoice" as const), withholding_eligible: row.withholding_eligible };
  }, [docTypesData, doc?.doc_type]);

  // 見積書ライブ計算の資格別源泉徴収マップ（document-type-master-phase3 設計書 §1.4）。
  // estimate 以外では computeDocument() 内で無視されるだけなので doc_type による分岐は不要。
  // reviewer 指摘: docTypesData 未取得時に {} を返すと computeEstimate() の
  // `professionWithholding ?? DEFAULT` という nullish フォールバックに引っかからず、
  // 見積書の全金額が ¥0 になってしまう。未取得・空の場合は undefined を返し、
  // 呼び出し側の既定値（DEFAULT_ESTIMATE_WITHHOLDING）にフォールバックさせる。
  const estimateWithholdingMap = useMemo<ProfessionWithholdingMap | undefined>(() => {
    if (!docTypesData) return undefined;
    const map: Record<string, boolean> = {};
    for (const d of docTypesData.data) {
      if (d.kind === "invoice" && d.profession) map[d.profession] = d.withholding_eligible;
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }, [docTypesData]);

  // ライブ計算: 入力中の明細行 + adjustmentAmount から即時再計算する。
  // 発行PDF（サーバー側 computeDocument）と同一実装を @juro/shared から使い、
  // doc_type 別の計算（見積書の業種別消費税・源泉）と apply_withholding
  // （源泉ルール×顧客種別）を反映する。画面とPDFの金額不一致を防ぐ。
  const liveComputed = useMemo(() => {
    if (!doc) return null;
    const docActiveLines = lines.length > 0 ? lines : toLineRows(doc.line_items);
    const calcLines = docActiveLines.map((l) => ({
      profession: l.profession ?? null,
      fee_amount: Math.floor(Number(l.fee_amount)) || 0,
      tax_amount: Math.floor(Number(l.tax_amount)) || 0,
    }));
    const computed = computeDocument(
      doc.doc_type, calcLines, adjustmentAmount, doc.apply_withholding ?? true, liveCalcConfig, estimateWithholdingMap,
    );
    return {
      ...computed,
      gross: computed.fee_subtotal + computed.tax_subtotal,
      adjustment_amount: adjustmentAmount,
    };
  }, [doc, lines, adjustmentAmount, liveCalcConfig, estimateWithholdingMap]);

  // 料金表ベースの書類のライブ計算。draft は現在の料金表＋入力から即時計算し、
  // issued は保存時スナップショット（calc_result_json）を正とする
  // （発行後に料金表が変わっても表示金額が変わらないように）。
  const feeCalcLive = useMemo<FeeCalcResult | null>(() => {
    if (!doc || !feeDocType) return null;
    if (doc.status !== "draft") return doc.calc_result ?? null;
    if (!feeSchedule || !calcInput) return doc.calc_result ?? null;
    try {
      return computeFeeCalc(feeSchedule, calcInput);
    } catch {
      return doc.calc_result ?? null;
    }
  }, [doc, feeDocType, feeSchedule, calcInput]);

  // v2テンプレートがある場合はHTMLプレビューを生成（WYSIWYGプレビュー）
  // ※ フックは早期 return の前に置く必要がある（Rules of Hooks）
  const htmlPreview = useMemo((): string | null => {
    if (!doc) return null;
    const baseTmpl = templateForDoc?.template_json;
    if (!baseTmpl || baseTmpl.schema_version !== 2) return null;
    // blocksDraft > blocks_snapshot > テンプレート既定 の優先順でアクティブブロックを決定
    const activeBlocksSrc: AnyBlock[] | null =
      blocksDraft
      ?? (doc.blocks_snapshot ? JSON.parse(doc.blocks_snapshot) as AnyBlock[] : null)
      ?? (baseTmpl.body?.blocks ?? null);
    if (!activeBlocksSrc) return null;
    // ② 簡易設定の射影: draft のみ後勝ち適用。issued は発行時に射影済みの
    // blocks_snapshot が凍結されているため再適用しない（detail.md §3.3）
    const rawTmpl = { ...baseTmpl, body: { blocks: activeBlocksSrc } };
    const tmpl = doc.status === "draft" ? applySimpleSettings(rawTmpl) : rawTmpl;
    // 資格者情報: 発行済みは発行時に凍結されたスナップショット（issuer_snapshot_json）が正。
    // draft は書類ごとの上書き（selectedHolder）> tenant_professions の既定担当者のライブ値。
    // （発行済みの htmlPreview が tenant_professions の「今の」値を見てしまうと、資格者を
    // 差し替えた後に過去の発行済み書類の表示まで変わってしまう＝電帳法②のスナップショット
    // 原則に反するため、必ず issuer_snapshot_json を優先する）
    let issuedSnapshot: {
      qualification_label: string; holder_name: string; invoice_registration_number: string;
      rules: { field_key: string; value: string }[];
    } | null = null;
    if (doc.status !== "draft" && doc.issuer_snapshot_json) {
      try {
        const parsed = JSON.parse(doc.issuer_snapshot_json);
        if (parsed && Array.isArray(parsed.rules)) issuedSnapshot = parsed;
      } catch {
        // 破損したJSONは無視してライブ値にフォールバック
      }
    }

    const holderName = issuedSnapshot?.holder_name
      ?? selectedHolder?.name ?? issuerProfession?.holder?.name ?? "";
    const invoiceRegNo = issuedSnapshot?.invoice_registration_number
      ?? selectedHolder?.invoice_registration_number
      ?? issuerProfession?.holder?.invoice_registration_number ?? "";
    const registrationNumber = issuedSnapshot?.rules.find((r) => r.field_key === "registration_number")?.value
      ?? selectedHolder?.professional_registration_number
      ?? issuerProfession?.rules.find((r) => r.field_key === "registration_number")?.value
      ?? "";
    const businessLabel = issuedSnapshot?.rules.find((r) => r.field_key === "business_label")?.value
      ?? issuerProfession?.rules.find((r) => r.field_key === "business_label")?.value ?? "";
    const qualificationLabel = issuedSnapshot?.qualification_label ?? issuerProfession?.qualification_label ?? "";
    const issuerRulesMap: Record<string, string> = {};
    for (const rule of issuedSnapshot?.rules ?? issuerProfession?.rules ?? []) {
      if (rule.value) issuerRulesMap[rule.field_key] = rule.value;
    }
    // M1: 請求日フォールバック規則: issueDate > issued_at > today
    const dateStr = issueDate
      || (doc.issued_at ? doc.issued_at.slice(0, 10) : new Date().toISOString().slice(0, 10));
    // B-2: 発行日は和暦表示（PDF側 document-pdf.ts と同一規則）
    const dateDisplay = formatWareki(dateStr);
    // ライブ計算値を優先。まだ計算前（null）なら保存済み値にフォールバック
    const live = liveComputed ?? doc.computed;
    const docActiveLines = lines.length > 0 ? lines : toLineRows(doc.line_items);
    // M1: 宛名フォールバック規則: recipient > case.client_name > ""
    const clientName = recipient || caseDetail?.client_name || "";
    const renderData: TemplateRenderData = {
      client: { name: clientName },
      office: applySimpleOffice({
        name: "登記測量 長坂・天野合同事務所",
        name_short: "登記測量",
        name_main: "長坂・天野合同事務所",
        postal: "444-0853",
        address: "岡崎市三崎町9番地5",
        tel: "0564-52-8530",
        fax: "0564-51-7996",
      }, doc.status === "draft" ? simpleSettings : undefined),
      invoice: {
        number: doc.doc_number,
        registration_number: invoiceRegNo,
        date: dateDisplay,
      },
      issuer: {
        qualification: qualificationLabel,
        business: businessLabel,
        business_label: businessLabel,
        name: holderName,
        registration_number: registrationNumber,
        rules: issuerRulesMap,
      },
      transaction: { date: dateDisplay },
      amount: {
        invoice: live.total,
        fee: live.fee_subtotal,
        consumption_tax: live.consumption_tax,
        withholding: live.withholding_tax,
      },
      computed: {
        fee_subtotal: live.fee_subtotal,
        tax_subtotal: live.tax_subtotal,
        gross: live.fee_subtotal + live.tax_subtotal,
        consumption_tax: live.consumption_tax,
        withholding_tax: live.withholding_tax,
        total: live.total,
        adjustment_amount: adjustmentAmount,
      },
      line_items: docActiveLines.map((li) => ({
        item_date: li.item_date ?? null,
        description: li.description,
        fee_amount: Math.floor(Number(li.fee_amount) || 0),
        tax_amount: Math.floor(Number(li.tax_amount) || 0),
      })),
      // M1: 備考（空なら②の既定文 → 従来の振込案内文言の順でフォールバック）
      // 見積書は請求行為ではないため、既定の振込手数料注記は入れない（document-pdf.ts と同一規則）
      notes: notes || simpleSettings?.boilerplate?.footer_note
        || (doc.doc_type === "estimate" ? "" : DEFAULT_TRANSFER_NOTE),
    };
    try {
      return serializeBlocksToHtml(tmpl, renderData);
    } catch {
      return null;
    }
  }, [templateForDoc, doc, caseDetail, lines, blocksDraft, liveComputed, adjustmentAmount,
      recipient, issueDate, notes, simpleSettings, issuerProfession, selectedHolder]);

  if (isLoading) return <div style={{ padding: 24, color: "#94a3b8" }}>読み込み中…</div>;
  if (!doc) return <div style={{ padding: 24, color: "#dc2626" }}>書類が見つかりません</div>;

  const isDraft    = doc.status === "draft";
  const isIssued   = doc.status === "issued";
  // estimate-invoice-sync 設計書 §3.3/3.4: 請求書編集時は同期カード、見積書 draft 編集時は案内バーを出す
  const isInvoiceDoc = doc.doc_type.endsWith("_invoice") && doc.doc_type !== "guarantee_invoice";
  const isEstimateDraft = doc.doc_type === "estimate" && isDraft;
  const isEstimate = doc.doc_type === "estimate";
  // 料金表ベースの書類（土地/建物計算書・保証協会請求書）: 構造化入力エディタで編集する
  const isFeeCalcDoc = feeDocType !== null;
  // document-create-flow §6-4(a): 見積書表示時のみ、現行の請求書（superseded されていない
  // 非キャンセル）へのナビゲーションボタンを出す。見積書の集約対象（estimate-aggregate.ts）と同じ条件。
  // Phase 3（document-type-master-phase3.md §1.9）: 既存3資格固定の RELATED_INVOICE_TYPES から
  // tenant_document_types（kind='invoice' かつ profession が設定されている doc_type）の動的一覧に置き換え。
  const relatedInvoiceDocTypes = new Set(
    (docTypesData?.data ?? []).filter((d) => d.kind === "invoice" && d.profession !== null).map((d) => d.doc_type),
  );
  const relatedInvoices: DocumentSummary[] = isEstimate && caseDetail
    ? caseDetail.documents.filter(
        (d) =>
          relatedInvoiceDocTypes.has(d.doc_type) &&
          d.status !== "cancelled" &&
          !d.superseded_by_id,
      )
    : [];

  // プリセットが利用可能な doc_type かどうかを判定。
  // 料金表ベースの書類（土地/建物計算書・保証協会請求書）のPDFは TemplateV2 のグリッドではなく
  // 計算結果スナップショット専用のレンダラで描画される（document-pdf.ts）。
  // プリセットを適用しても発行PDFの体裁は変わらないので、ここでは対象外にする。
  const presetAvailable = presetDocTypes.has(doc.doc_type) && !isFeeCalcDoc;
  // 現在のテンプレートが見本準拠かどうか（fee_amount / item_date 列があればプリセット互換とみなす）
  const templateIsPresetCompatible = (() => {
    if (!templateForDoc) return false;
    const grid = pickGridBlock(templateForDoc);
    if (!grid) return false;
    const colIds = grid.columns.map((c) => c.id);
    return colIds.includes("fee_amount") || colIds.includes("item_date");
  })();
  // バナーで「見本レイアウトを適用」ボタンを表示するか
  const showPresetButton = presetAvailable && !templateIsPresetCompatible && isDraft;
  const computed   = doc.computed;
  const templateData = buildTemplateData(doc, caseDetail);
  const activeLines = lines.length > 0 ? lines : toLineRows(doc.line_items);
  // 表示に使うブロック: 編集中→draft / 保存済みスナップショット→それ / なければテンプレート
  const activeBlocks: AnyBlock[] | null =
    (editing && blocksDraft) ? blocksDraft
    : doc.blocks_snapshot ? (JSON.parse(doc.blocks_snapshot) as AnyBlock[])
    : templateBlocks;
  const linePayload = toNewLineItems(activeLines);
  const savedLinePayload = doc.line_items.map((l) => ({
    line_order: l.line_order,
    profession: l.profession,
    item_date: l.item_date ?? undefined,
    description: l.description,
    fee_amount: l.fee_amount,
    tax_amount: l.tax_amount,
  }));
  const hasUnsavedLineChanges = JSON.stringify(linePayload) !== JSON.stringify(savedLinePayload);

  async function handleDownloadPdf() {
    setPdfLoading(true);
    try {
      // 画面にPDFプレビューが出ている場合は、そのBlobをそのまま保存する。
      // これにより「プレビューで見たPDF」と「ダウンロードしたPDF」がずれない。
      let blob = pdfPreviewBlob;
      if (!blob) {
        // 発行済みは確定PDF（R2保存・pdf_sha256記録済み）を取得する。再生成はしない。
        // draft はその時点の内容で都度生成する。
        blob = doc?.status === "issued"
          ? await fetchIssuedPdf(id!)
          : htmlPreview
            ? await documentsApi.generatePdf(id!, { force: true })
            : await documentsApi.getPdf(id!);
        setPdfPreviewBlob(blob);
        setPdfPreviewFilename(`${doc?.doc_number ?? id}.pdf`);
      }
      const url = URL.createObjectURL(blob);
      const filename = pdfPreviewBlob
        ? (pdfPreviewFilename ?? `${doc?.doc_number ?? id}.pdf`)
        : `${doc?.doc_number ?? id}.pdf`;
      Object.assign(document.createElement("a"), { href: url, download: filename }).click();
      URL.revokeObjectURL(url);
      toast("PDFをダウンロードしました");
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setPdfLoading(false);
    }
  }

  function openAdvancedTemplateSettings() {
    setSimpleTemplateOpen(false);
    if (templateForDoc) {
      navigate(`/settings/templates/${templateForDoc.id}`);
      return;
    }
    createTemplateMut.mutate();
  }

  return (
    <div style={{ padding: 24 }}>
      {/* M0: DocumentHeader (パンくず + ヘッダーカード) */}
      <DocumentHeader
        doc={doc}
        caseDetail={caseDetail}
        isDraft={isDraft}
        isIssued={isIssued}
        editing={editing}
        blocksDraft={blocksDraft}
        activeBlocks={activeBlocks}
        savePending={saveMut.isPending}
        saveAndIssuePending={saveAndIssueMut.isPending}
        pdfPreviewLoading={pdfPreviewLoading}
        pdfLoading={pdfLoading}
        onCancelEdit={() => {
          setLines(toLineRows(doc.line_items));
          setAdjustmentAmount(doc.adjustment_amount ?? 0);
          setEditing(false);
          setBlocksDraft(null);
          if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
          setPdfPreviewUrl(null);
          setPdfPreviewBlob(null);
          setPdfPreviewFilename(null);
          setRecipient(doc.recipient_name ?? "");
          setIssueDate(doc.issue_date ?? "");
          setNotes(doc.notes ?? "");
          setIssuerHolderId(doc.issuer_holder_id ?? null);
          setCalcInput(isFeeScheduleDocType(doc.doc_type) ? (doc.calc_input ?? emptyFeeCalcInput(doc.doc_type)) : null);
        }}
        onSave={() => saveMut.mutate()}
        onSaveAndIssue={() => saveAndIssueMut.mutate()}
        onOpenTemplateSettings={() => setSimpleTemplateOpen(true)}
        onStartEdit={startEdit}
        onOpenRevise={() => setReviseModal(true)}
        onOpenCancel={() => setCancelModal(true)}
        onQuickRevise={() => quickReviseMut.mutate()}
        quickRevisePending={quickReviseMut.isPending}
      />

      {/* 実施業務からの一括作成で作った書類を1件ずつ仕上げるための巡回ナビ */}
      {queueEntries && queueEntries.length > 1 && (
        <DocumentQueueNav
          entries={queueEntries}
          nextPendingId={queueNextPendingId}
          editing={editing && isDraft}
          onOpen={(docId) => navigate(`/documents/${docId}`)}
          onFinish={() => {
            if (doc.case_id) clearDocQueue(doc.case_id);
            navigate(`/cases/${doc.case_id}/pdfs`);
          }}
          onDismiss={dismissQueue}
        />
      )}

      {/* 請求書画面の「見積書を開く」から来た場合、元の請求書への戻る導線を出す */}
      {isEstimate && fromDocumentId && (
        <div style={{ marginBottom: 12 }}>
          <Button
            variant="outline"
            style={{ minHeight: "auto", padding: "6px 14px", fontSize: 13, fontWeight: 600 }}
            onClick={() => navigate(`/documents/${fromDocumentId}`)}
          >
            ← 請求書に戻る
          </Button>
        </div>
      )}

      {/* document-create-flow §6-4(a): 見積書表示時のみ、現行請求書への相互ナビゲーション */}
      {isEstimate && relatedInvoices.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
          <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>関連書類:</span>
          {relatedInvoices.map((d) => (
            <Button
              key={d.id}
              variant="outline"
              style={{ minHeight: "auto", padding: "4px 10px", fontSize: 12, fontWeight: 600 }}
              onClick={() => navigate(`/documents/${d.id}`)}
            >
              {getDocTypeLabel(d.doc_type)}（{d.doc_number ?? "下書き"}）
            </Button>
          ))}
        </div>
      )}

      <SimpleTemplateDrawer
        open={simpleTemplateOpen}
        docTypeLabel={getDocTypeLabel(doc.doc_type)}
        templateName={templateForDoc?.name}
        settings={simpleSettings}
        saving={saveSimpleSettingsMut.isPending}
        onSave={(s) => saveSimpleSettingsMut.mutate(s)}
        onClose={() => setSimpleTemplateOpen(false)}
        onOpenAdvanced={openAdvancedTemplateSettings}
      />

      {/* M0: DocumentStatusGuide (draft/issued バナー) */}
      <DocumentStatusGuide
        isDraft={isDraft}
        isIssued={isIssued}
        isEstimate={isEstimate}
        justIssued={justIssued}
        editing={editing}
        templateForDoc={templateForDoc}
        docTypeLabel={getDocTypeLabel(doc.doc_type)}
        presetAvailable={presetAvailable}
        showPresetButton={showPresetButton}
        createTemplatePending={createTemplateMut.isPending}
        installPresetPending={installPresetMut.isPending}
        pdfLoading={pdfLoading}
        onCreateTemplate={() => createTemplateMut.mutate()}
        onInstallPreset={() => installPresetMut.mutate()}
        onDownloadPdf={handleDownloadPdf}
      />

      {/* 発行者情報（登録番号など）が未入力のまま発行されるのを防ぐ警告 */}
      {isDraft && issuerProfessionKey && missingIssuerRefs.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 16px", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: "#c2410c", fontWeight: 700, minWidth: 0 }}>
            この書類のPDFには{issuerProfession?.qualification_label ?? issuerProfessionKey}の発行者情報が印字されますが、未入力の項目があります:
            {" "}{missingIssuerRefs.map((ref) => issuerRefLabel(ref, issuerProfession)).join("、")}
          </div>
          <Button onClick={() => setShowIssuerDialog(true)} style={{ flexShrink: 0 }}>今すぐ登録</Button>
        </div>
      )}

      {showIssuerDialog && issuerProfessionKey && (
        <IssuerMasterDialog docType={doc.doc_type} referencedKeys={issuerRefs} onClose={() => setShowIssuerDialog(false)} />
      )}

      {/* 書類編集面 */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, marginBottom: 20, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px 12px", borderBottom: "1px solid #f1f5f9" }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>
            {isEstimate
              ? (editing ? "見積書の内容を編集" : "見積書の内容")
              : isFeeCalcDoc
              ? (editing ? `${getDocTypeLabel(doc.doc_type)}の内容を編集` : `${getDocTypeLabel(doc.doc_type)}の内容`)
              : (editing ? "請求書の内容を編集" : "請求書の内容")}
          </h2>
          {isDraft && (
            <button type="button" style={btnStyle("ghost")} onClick={() => setSimpleTemplateOpen(true)}>
              体裁を整える
            </button>
          )}
        </div>

        {/* estimate-invoice-sync §3.4: 見積書 draft 編集時、請求書集約との同期状態を案内 */}
        {isEstimateDraft && estimateStatus && (estimateStatus.sync === "in_sync" || estimateStatus.sync === "stale") && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
            padding: "8px 20px", fontSize: 12,
            background: estimateStatus.sync === "stale" ? "#fffbeb" : "#f0fdfa",
            color: estimateStatus.sync === "stale" ? "#92400e" : "#134e4a",
            borderBottom: "1px solid #f1f5f9",
          }}>
            <span>
              {estimateStatus.sync === "stale"
                ? "この見積書の明細は請求書の合算内容と異なります（手動編集された可能性があります）。請求書側を保存すると合算内容で自動的に上書きされます。"
                : "この見積書は請求書の内容から自動で更新されます。手動で編集した明細は、請求書側を保存すると上書きされます。"}
            </span>
          </div>
        )}

        {isFeeCalcDoc && calcInput ? (
          // 料金表ベースの書類: 構造化入力エディタ（明細・金額は料金表から自動計算）
          <div style={{ padding: 20 }}>
            {isDraft && editing && (
              <div style={{ marginBottom: 16 }}>
                <InvoiceContentForm
                  recipient={recipient}
                  onRecipientChange={setRecipient}
                  recipientFallback={caseDetail?.client_name ?? ""}
                  issueDate={issueDate}
                  onIssueDateChange={setIssueDate}
                  notes={notes}
                  onNotesChange={setNotes}
                  profession={issuerProfessionKey && showHolderSelector ? { key: issuerProfessionKey, label: issuerProfession?.qualification_label ?? issuerProfessionKey } : undefined}
                  holderId={issuerHolderId}
                  onHolderIdChange={setIssuerHolderId}
                  holderOptions={holderOptions}
                  defaultHolderName={issuerProfession?.holder?.name}
                  editable={true}
                />
              </div>
            )}
            {isDraft && editing && !feeSchedule && feeScheduleError ? (
              // 料金表の取得に失敗すると編集不能のまま固まるため、リトライ導線を出す
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, fontSize: 13, color: "#c2410c" }}>
                料金表の取得に失敗しました。
                <Button variant="outline" onClick={() => refetchFeeSchedule()}>再試行</Button>
              </div>
            ) : calcInput.kind === "guarantee" ? (
              <GuaranteeCalcEditor
                schedule={feeSchedule?.kind === "guarantee" ? feeSchedule : null}
                input={calcInput}
                result={feeCalcLive?.kind === "guarantee" ? feeCalcLive : null}
                onChange={setCalcInput}
                editable={isDraft && editing}
              />
            ) : (
              <WorkRateCalcEditor
                schedule={feeSchedule?.kind === "work_rate" ? feeSchedule : null}
                input={calcInput}
                result={feeCalcLive?.kind === "work_rate" ? feeCalcLive : null}
                onChange={setCalcInput}
                editable={isDraft && editing}
              />
            )}
          </div>
        ) : htmlPreview ? (
          // v2テンプレートあり: WYSIWYGレイアウト（DocBlockPanel は書類編集に不要なので非表示）
          <>
            {/* M1: 宛名・請求日・備考の内容編集フォーム（編集中のみ表示） */}
            {isDraft && editing && (
              <div style={{ padding: "16px 20px 20px", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 12 }}>
                  {isEstimate ? "見積書の内容" : "請求書の内容"}
                </div>
                <InvoiceContentForm
                  recipient={recipient}
                  onRecipientChange={setRecipient}
                  recipientFallback={caseDetail?.client_name ?? ""}
                  issueDate={issueDate}
                  onIssueDateChange={setIssueDate}
                  notes={notes}
                  onNotesChange={setNotes}
                  profession={issuerProfessionKey && showHolderSelector ? { key: issuerProfessionKey, label: issuerProfession?.qualification_label ?? issuerProfessionKey } : undefined}
                  holderId={issuerHolderId}
                  onHolderIdChange={setIssuerHolderId}
                  holderOptions={holderOptions}
                  defaultHolderName={issuerProfession?.holder?.name}
                  editable={true}
                />
              </div>
            )}
            {/* 明細・調整額・集計の入力エリア（編集中のみ表示） */}
            {isDraft && editing && (
              <div style={{ padding: "16px 20px 20px", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 10 }}>明細の入力</div>
                <LineItemEditor
                  lines={activeLines}
                  docType={doc.doc_type}
                  onChange={setLines}
                  templateGrid={templateGrid}
                  editable={true}
                />
                {/* M0: AmountSummary コンポーネントに置き換え */}
                <AmountSummary
                  liveComputed={liveComputed}
                  adjustmentAmount={adjustmentAmount}
                  onAdjustmentChange={setAdjustmentAmount}
                  totalLabel={isEstimate ? "お見積金額（税・源泉込み）" : undefined}
                />
                {/* estimate-invoice-sync §3.3: 請求書編集時、見積書との同期状態を可視化 */}
                {isInvoiceDoc && <EstimateSyncCard caseId={doc.case_id} context="invoice-page" fromDocumentId={doc.id} />}
              </div>
            )}
            <DocumentPreview
              htmlPreview={htmlPreview}
              isDraft={isDraft}
              editing={editing}
            />
          </>
        ) : (
          // フォールバック: v2テンプレートなし
          <div style={{ display: "flex" }}>
            {editing && blocksDraft && (
              <DocBlockPanel
                blocks={blocksDraft}
                selectedBlockId={selectedDocBlockId}
                onSelect={setSelectedDocBlockId}
                onAdd={handleAddBlock}
                onMove={handleMoveBlock}
                onDelete={handleDeleteBlock}
              />
            )}
            <div style={{ flex: 1, minWidth: 0, padding: 20 }}>
              {activeBlocks ? (
                <TemplateBlocksEditor
                  blocks={activeBlocks}
                  lines={activeLines}
                  docType={doc.doc_type}
                  onChange={setLines}
                  data={templateData}
                  editable={isDraft && editing}
                  onBlockChange={isDraft && editing && blocksDraft ? handleBlockChange : undefined}
                  selectedBlockId={selectedDocBlockId}
                />
              ) : (
                <LineItemEditor
                  lines={activeLines}
                  docType={doc.doc_type}
                  onChange={setLines}
                  templateGrid={templateGrid}
                  editable={isDraft && editing}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* M0: AmountSummary（発行済み・または v2 テンプレートなしの場合に表示。
          料金表ベースの書類はエディタ内の①〜⑨集計が正のため表示しない） */}
      {!isFeeCalcDoc && (!htmlPreview || !isDraft) && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, marginTop: 0, marginBottom: 14 }}>計算結果</h2>
          <AmountSummary
            liveComputed={liveComputed ?? {
              fee_subtotal: computed.fee_subtotal,
              tax_subtotal: computed.tax_subtotal,
              gross: computed.fee_subtotal + computed.tax_subtotal,
              consumption_tax: computed.consumption_tax,
              withholding_tax: computed.withholding_tax,
              total: computed.total,
              adjustment_amount: adjustmentAmount,
            }}
            adjustmentAmount={adjustmentAmount}
            totalLabel={isEstimate ? "お見積金額（税・源泉込み）" : undefined}
          />
        </div>
      )}

      {/* estimate-invoice-sync §3.3: 請求書編集時、見積書との同期状態を可視化
          （編集中の htmlPreview パス以外＝プレビューのみ表示中・非draft・フォールバック表示のいずれでも出す） */}
      {isInvoiceDoc && !(htmlPreview && isDraft && editing) && (
        <div style={{ marginTop: 16 }}>
          <EstimateSyncCard caseId={doc.case_id} context="invoice-page" fromDocumentId={doc.id} />
        </div>
      )}

      {/* PDF プレビュー */}
      {pdfPreviewLoading && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 32, marginBottom: 20, textAlign: "center", color: "#64748b", fontSize: 14 }}>
          PDFを生成中…
        </div>
      )}
      {pdfPreviewUrl && !pdfPreviewLoading && (
        <div ref={pdfPreviewRef} style={{ background: "#fff", border: "1px solid #0f766e", borderRadius: 12, marginBottom: 20, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderBottom: "1px solid #e2e8f0", background: "#f0fdfa" }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: "#0f766e" }}>PDFプレビュー（このPDFをダウンロードします）</h2>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={btnStyle("outline")} onClick={handleDownloadPdf} disabled={pdfLoading}>
                ダウンロード
              </button>
            </div>
          </div>
          <PdfViewer url={pdfPreviewUrl} />
        </div>
      )}


      {/* 入金管理 */}
      {isIssued && (() => {
        const payments = paymentsData?.data ?? [];
        const paidTotal = payments.reduce((s, p) => s + p.amount, 0);
        const remaining = computed.total - paidTotal;
        const isPaidFull = remaining <= 0;

        return (
          <div style={{ background: "#fff", border: `1px solid ${isPaidFull ? "#bbf7d0" : "#e2e8f0"}`, borderRadius: 12, padding: 20, marginTop: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>入金管理</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {isPaidFull ? (
                  <span style={{ fontSize: 12, background: "#dcfce7", color: "#15803d", padding: "3px 10px", borderRadius: 99, fontWeight: 600 }}>入金完了</span>
                ) : (
                  <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>残高：{formatJPY(remaining)}</span>
                )}
                {!payForm && (
                  <button style={btnStyle("outline")} onClick={() => setPayForm({
                    paid_date: new Date().toISOString().slice(0, 10),
                    bank_name: "",
                    amount: String(remaining > 0 ? remaining : 0),
                    memo: "",
                  })}>+ 入金を記録</button>
                )}
              </div>
            </div>

            {payForm && (
              <form onSubmit={(e) => {
                e.preventDefault();
                addPayMut.mutate({
                  paid_date: payForm.paid_date,
                  bank_name: payForm.bank_name || undefined,
                  amount: Number(payForm.amount),
                  memo: payForm.memo || undefined,
                });
              }} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, marginBottom: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>入金日 *</span>
                  <input type="date" required value={payForm.paid_date} onChange={(e) => setPayForm({ ...payForm, paid_date: e.target.value })}
                    style={inputStyle} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>金額 *</span>
                  <input type="number" required min={1} value={payForm.amount}
                    onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                    style={{ ...inputStyle, width: 130, textAlign: "right" }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>口座・支払方法</span>
                  <input type="text" value={payForm.bank_name} placeholder="例：岡信本店"
                    onChange={(e) => setPayForm({ ...payForm, bank_name: e.target.value })}
                    style={{ ...inputStyle, width: 140 }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>メモ</span>
                  <input type="text" value={payForm.memo}
                    placeholder="入金メモ"
                    onChange={(e) => setPayForm({ ...payForm, memo: e.target.value })}
                    style={{ ...inputStyle, width: 160 }} />
                </label>
                <div style={{ display: "flex", gap: 6, paddingBottom: 1 }}>
                  <button type="button" style={btnStyle("outline")} onClick={() => setPayForm(null)}>キャンセル</button>
                  <button type="submit" style={btnStyle("primary")} disabled={addPayMut.isPending}>記録する</button>
                </div>
              </form>
            )}

            {payments.length === 0 ? (
              <p style={{ fontSize: 13, color: "#94a3b8", margin: 0 }}>入金記録がありません</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                    <th style={thStyle}>入金日</th>
                    <th style={thStyle}>口座・支払方法</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>金額</th>
                    <th style={thStyle}>メモ</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={tdStyle}>{p.paid_date}</td>
                      <td style={tdStyle}>{p.bank_name ?? "—"}</td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{formatJPY(p.amount)}</td>
                      <td style={{ ...tdStyle, color: "#64748b" }}>{p.memo ?? "—"}</td>
                      <td style={tdStyle}>
                        <button onClick={() => { if (confirm("この入金記録を削除しますか？")) deletePayMut.mutate(p.id); }}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 16, padding: "0 4px" }}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: "2px solid #e2e8f0" }}>
                    <td colSpan={2} style={{ ...tdStyle, fontWeight: 600, color: "#475569" }}>入金合計</td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>{formatJPY(paidTotal)}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        );
      })()}

      {/* テンプレートとして保存モーダル */}
      {saveAsTemplateModal && (
        <Modal title="テンプレートとして保存" onClose={() => { setSaveAsTemplateModal(false); setSaveAsTemplateName(""); }}>
          <form onSubmit={(e) => { e.preventDefault(); saveAsTemplateMut.mutate(); }}
                style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ margin: 0, fontSize: 13, color: "#475569" }}>
              この書類のブロック構成を新しいテンプレートとして保存します。<br />
              保存後、テンプレート管理画面で列定義や計算式を編集できます。
            </p>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>テンプレート名</span>
              <input
                type="text"
                value={saveAsTemplateName}
                onChange={(e) => setSaveAsTemplateName(e.target.value)}
                placeholder={`${getDocTypeLabel(doc.doc_type)}テンプレート`}
                style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14 }}
              />
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={btnStyle("outline")} onClick={() => { setSaveAsTemplateModal(false); setSaveAsTemplateName(""); }}>キャンセル</button>
              <button type="submit" style={btnStyle("primary")} disabled={saveAsTemplateMut.isPending}>
                {saveAsTemplateMut.isPending ? "作成中…" : "テンプレートを作成"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* 取消モーダル */}
      {cancelModal && (
        <Modal title="書類を取消" onClose={() => setCancelModal(false)}>
          <form onSubmit={(e) => { e.preventDefault(); cancelMut.mutate(); }}
                style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>取消理由 *</span>
              <input type="text" required value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                placeholder="例：重複作成のため"
                style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14 }} />
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={btnStyle("outline")} onClick={() => setCancelModal(false)}>戻る</button>
              <button type="submit" style={btnStyle("danger")} disabled={cancelMut.isPending}>取消する</button>
            </div>
          </form>
        </Modal>
      )}

      {/* 修正版モーダル */}
      {reviseModal && (
        <Modal title="修正版を作成" onClose={() => setReviseModal(false)}>
          <form onSubmit={(e) => { e.preventDefault(); reviseMut.mutate(); }}
                style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ margin: 0, fontSize: 13, color: "#475569" }}>
              現在の明細をコピーした新しい下書き（v{doc.version + 1}）を作成します。
            </p>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>修正理由 *</span>
              <input type="text" required value={reviseReason} onChange={(e) => setReviseReason(e.target.value)}
                placeholder="例：明細金額を修正"
                style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14 }} />
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={btnStyle("outline")} onClick={() => setReviseModal(false)}>戻る</button>
              <button type="submit" style={btnStyle("primary")} disabled={reviseMut.isPending}>作成する</button>
            </div>
          </form>
        </Modal>
      )}

      {/* document-create-flow §6-2: 請求書発行成功後の見積書案内モーダル（発行のたびに1回だけ表示） */}
      {showEstimateGuide && isInvoiceDoc && estimateStatus &&
        (estimateStatus.sync === "stale" || estimateStatus.sync === "in_sync" || estimateStatus.sync === "not_created") && (
        <Modal
          title={estimateGuideReason === "prompt" ? "見積書も作成しますか？" : "請求書を発行しました"}
          onClose={() => setShowEstimateGuide(false)}
        >
          <p style={{ margin: "0 0 16px", fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
            {estimateStatus.sync === "stale" &&
              "続けて見積書を更新しますか？見積書がこの請求書の内容とまだ一致していません。更新すると、お客様に渡す見積書（総額）と請求書が揃います。"}
            {estimateStatus.sync === "in_sync" &&
              "続けて見積書を開きますか？見積書は請求書の内容と一致しています。開いて発行すると PDF をダウンロードできます。"}
            {estimateStatus.sync === "not_created" &&
              "続けて見積書を作成しますか？この案件の見積書はまだ作成されていません。請求書の合算から自動作成します。"}
          </p>
          <p style={{ margin: "0 0 16px", fontSize: 11, color: "#94a3b8", lineHeight: 1.6 }}>
            見積書は 案件画面の書類一覧、またはこの画面下部の同期カードからいつでも開けます。
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="outline" onClick={() => setShowEstimateGuide(false)}>あとで</Button>
            {estimateStatus.sync === "stale" && (
              <Button variant="primary" disabled={estimateGuideMut.isPending} onClick={() => estimateGuideMut.mutate("refresh")}>
                {estimateGuideMut.isPending ? "更新中…" : "見積書を更新して開く"}
              </Button>
            )}
            {estimateStatus.sync === "in_sync" && (
              <Button variant="primary" onClick={() => { setShowEstimateGuide(false); navigate(`/documents/${estimateStatus.estimate!.id}`); }}>
                見積書を開く
              </Button>
            )}
            {estimateStatus.sync === "not_created" && (
              <Button variant="primary" disabled={estimateGuideMut.isPending} onClick={() => estimateGuideMut.mutate("create")}>
                {estimateGuideMut.isPending ? "作成中…" : "見積書を作成して開く"}
              </Button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
